// @flow
// Редактор лекала: холст с линейками в мм и инструменты как в CorelDRAW:
// выбор, прямоугольник, эллипс, ломаная, фигура по размеру. Выбранное можно
// двигать, менять размер полями и за маркеры, поворачивать, отражать,
// скруглять углы, ставить по центру, копировать и вставлять (в том числе в
// другое лекало), удалять, отменять. Сохраняет новым лекалом рядом с
// исходным, исходный .prn не меняется.
//
// Координаты — единицы плоттера (0,1 мм), ось Y вверх, как в CorelDRAW.
// После каждого действия координаты округляются до целых: размер на экране
// равен тому, что уйдёт в файл.
//
// Контуры на холсте: «c3» — контур файла (координаты в xy, а перестроенный
// целиком, после скругления или смены начала реза, — в replaced), «x0» —
// новый контур (копия, вставка, фигура, ломаная) в extra: { cmds, shape }.
// У фигуры (shape) свои размер, центр, угол и радиус углов: при растяжении
// прямоугольника углы не искажаются.
//
// mode="create" — новое лекало: файл без контуров (шапка от соседнего
// лекала модели), при сохранении лекало встаёт в угол листа, как все лекала.
//
// Направление реза: у каждого контура точка начала, стрелки и номер по
// порядку реза. Нож режет контуры по порядку файла, каждый от первой точки.
import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  DEFAULT_CORNER,
  UNITS_PER_MM,
  SHIFT_THRESHOLD,
  anchorPoint,
  applyTransform,
  cmdsSegments,
  cmdsVertices,
  contourCmds,
  contourSegments,
  fillPaths,
  filletCmds,
  flattenSegments,
  formatMm,
  pairIndex,
  parseMm,
  restartCmds,
  reverseCmds,
  rotateAbout,
  scaleAbout,
  segmentsBox,
  segmentsToSvg,
  serializePrn,
  shapeCmds,
  transformCmds,
  transformShape,
  translate,
} from '../prn/prnModel';
import {
  booleanPaths,
  cornerAt,
  deleteNodes,
  dist,
  filletNodes,
  insertNode,
  intersectPaths,
  joinAt,
  moveHandle,
  moveNodes,
  nearestOnPath,
  pathFromCmds,
  pathNodes,
  pathToCmds,
  setPartType,
  splitPathAt,
  withoutPiece,
} from '../prn/prnPath';
import { renderPreviewPng } from '../prn/prnPreview';
import './VariantEditor.scss';

const RULER = 24;
const HANDLE = 8;
const LOCK_KEY = 'prnEditor.lockRatio';
const RADIUS_KEY = 'prnEditor.radius';
const CLIPBOARD_KEY = 'prnEditor.clipboard';
const DIRECTION_KEY = 'prnEditor.showDirection';
const SNAP_KEY = 'prnEditor.snap';
const GUIDES_KEY = 'prnEditor.guides.'; // + модель: направляющие общие для лекал модели
const SNAP_PX = 7;
const NEW_NAME = 'Новое лекало';
const emptyBox = () => ({ minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity });
const boxOk = b => b && b.minX <= b.maxX && b.minY <= b.maxY;
const unite = (a, b) => ({
  minX: Math.min(a.minX, b.minX), minY: Math.min(a.minY, b.minY),
  maxX: Math.max(a.maxX, b.maxX), maxY: Math.max(a.maxY, b.maxY),
});
const unionOf = list => list.reduce((b, it) => unite(b, it.box), emptyBox());
const boxArea = b => (b.maxX - b.minX) * (b.maxY - b.minY);
const biggest = list => list.reduce((a, b) => (boxArea(a.box) >= boxArea(b.box) ? a : b));

// Сдвиг всего лекала при сохранении. Лекала лежат в углу листа, почти у нуля;
// увеличение от центра уводит край за начало листа, и плоттер резал бы мимо
// плёнки. Если край ушёл за ноль дальше, чем у исходного, лекало целиком
// встаёт в тот же угол, что исходное. Форма реза не меняется.
function placementShift(box, orig) {
  if (!boxOk(box) || !boxOk(orig)) return [0, 0];
  const dx = box.minX < Math.min(0, orig.minX) ? Math.round(orig.minX - box.minX) : 0;
  const dy = box.minY < Math.min(0, orig.minY) ? Math.round(orig.minY - box.minY) : 0;
  return [dx, dy];
}

// Буфер обмена в localStorage: вставить можно и в другом лекале
function readClipboard() {
  try {
    const data = JSON.parse(localStorage.getItem(CLIPBOARD_KEY) || 'null');
    if (!data || !Array.isArray(data.items)) return null;
    const num = v => typeof v === 'number' && Number.isFinite(v);
    const cmdsOk = cmds => Array.isArray(cmds) && cmds.length > 0 && cmds.every(c => c
      && ['M', 'D', 'BZ'].includes(c.op) && Array.isArray(c.xy) && c.xy.length >= 2 && c.xy.every(num)
      && (c.op !== 'BZ' || c.xy.length === 8));
    const shapeOk = s => s && ['rect', 'ellipse'].includes(s.type) && [s.cx, s.cy, s.w, s.h].every(num);
    const items = data.items
      .filter(it => it && cmdsOk(it.cmds))
      .map(it => (shapeOk(it.shape) ? { cmds: it.cmds, shape: it.shape } : { cmds: it.cmds }));
    return items.length ? { from: data.from, items } : null;
  } catch (e) {
    return null;
  }
}

// === Состояние правок ===
// История — снимки { xy, deleted, extra, replaced }: объекты не меняются,
// каждое действие даёт новые

function snapshot(s) {
  return { xy: s.xy, deleted: s.deleted, extra: s.extra, replaced: s.replaced };
}

function reducer(s, a) {
  switch (a.type) {
    case 'select':
      return { ...s, selected: a.add ? { ...s.selected, ...a.keys } : a.keys };
    case 'toggle': {
      const selected = { ...s.selected };
      if (selected[a.key]) delete selected[a.key]; else selected[a.key] = true;
      return { ...s, selected };
    }
    case 'commit':
      return {
        ...s,
        xy: a.xy || s.xy,
        deleted: a.deleted || s.deleted,
        extra: a.extra || s.extra,
        replaced: a.replaced || s.replaced,
        selected: a.selected || s.selected,
        history: s.history.concat([snapshot(s)]).slice(-200),
        future: [],
      };
    case 'undo': {
      if (!s.history.length) return s;
      const prev = s.history[s.history.length - 1];
      return { ...s, ...prev, selected: {}, history: s.history.slice(0, -1), future: [snapshot(s)].concat(s.future) };
    }
    case 'redo': {
      if (!s.future.length) return s;
      const next = s.future[0];
      return { ...s, ...next, selected: {}, history: s.history.concat([snapshot(s)]), future: s.future.slice(1) };
    }
    default:
      return s;
  }
}

// Шаг линейки: крупные деления не чаще 56 px
const STEPS_MM = [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000];
function rulerStep(k) {
  const pxPerMm = k * UNITS_PER_MM;
  const major = STEPS_MM.find(s => s * pxPerMm >= 56) || 1000;
  const minorCandidates = [major / 10, major / 5, major / 2];
  const minor = minorCandidates.find(m => m * pxPerMm >= 7) || major;
  return { major, minor };
}

function NumField({ label, title, value, onCommit, disabled, suffix, width = 76 }) {
  const [text, setText] = useState('');
  const [focused, setFocused] = useState(false);
  const cancelled = useRef(false);
  useEffect(() => {
    if (!focused) setText(value === null || value === undefined ? '' : String(value));
  }, [value, focused]);
  const commit = () => {
    const v = parseMm(text);
    if (v !== null && String(v) !== String(parseMm(String(value)))) onCommit(v);
  };
  return (
    <label className="prn-editor__field" title={title}>
      <span>{label}</span>
      <input
        type="text"
        inputMode="decimal"
        value={text}
        disabled={disabled}
        style={{ width }}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          // Применяется один раз: здесь. Enter просто уводит фокус
          if (!cancelled.current) commit();
          cancelled.current = false;
          setFocused(false);
        }}
        onChange={e => setText(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') e.target.blur();
          if (e.key === 'Escape') {
            cancelled.current = true;
            setText(value === null ? '' : String(value));
            e.target.blur();
          }
        }}
      />
      {suffix && <em>{suffix}</em>}
    </label>
  );
}

// Контур (цепочка отрезков) в путь svg, в единицах плоттера
const pathD = path => path.parts.map((pt, i) => {
  const head = i === 0 ? `M${pt.p0[0]} ${pt.p0[1]} ` : '';
  return head + (pt.type === 'L' ? `L${pt.p1[0]} ${pt.p1[1]}` : `C${pt.c1[0]} ${pt.c1[1]} ${pt.c2[0]} ${pt.c2[1]} ${pt.p1[0]} ${pt.p1[1]}`);
}).join(' ');

const mmText = units => formatMm(units);
const mmValue = units => (Number.isFinite(units) ? formatMm(units) : null);
const degText = deg => String(Math.round((deg || 0) * 100) / 100).replace('.', ',');

// Инструменты слева, клавиши как в CorelDRAW
const TOOLS = [
  { id: 'select', title: 'Выбор и перемещение (Esc)', icon: <path d="M6 3 L6 19 L10.2 14.8 L13 21 L15.4 20 L12.6 13.8 L18.5 13.8 Z" /> },
  {
    id: 'node',
    title: 'Узлы (F10): двигать узлы и ручки кривых, добавлять и удалять узлы, скруглять отдельные углы',
    icon: (
      <>
        <path d="M5 19 C 7 9, 15 15, 19 5" />
        <rect x="3" y="17" width="4" height="4" />
        <rect x="17" y="3" width="4" height="4" />
        <path d="M11.5 12 L15 9" />
        <circle cx="15.5" cy="8.5" r="1.3" />
      </>
    ),
  },
  { id: 'rect', title: 'Прямоугольник (F6). Ctrl: квадрат, Shift: от центра', icon: <rect x="4" y="6" width="16" height="12" rx="1" /> },
  { id: 'ellipse', title: 'Эллипс (F7). Ctrl: круг, Shift: от центра', icon: <ellipse cx="12" cy="12" rx="8.5" ry="6.5" /> },
  { id: 'poly', title: 'Ломаная (F5). Щелчок по первой точке замыкает контур', icon: <path d="M4 18 L9 7 L14 15 L20 5" /> },
  {
    id: 'scissors',
    title: 'Удалить кусок: щёлкните по куску контура между пересечениями или выбранными узлами',
    icon: (
      <>
        <circle cx="6" cy="17" r="2.6" />
        <circle cx="17" cy="17" r="2.6" />
        <path d="M8 15 L16.5 4 M15 15 L7.5 4" />
      </>
    ),
  },
  {
    id: 'start',
    title: 'Начало реза: щёлкните по точке контура, с неё нож начнёт рез',
    icon: (
      <>
        <circle cx="7" cy="17" r="3" />
        <path d="M10 14 L18 6 M13 6 L18 6 L18 11" />
      </>
    ),
  },
];

const TOOL_HINTS = {
  select: 'Нажмите на контур, чтобы выбрать. Shift: добавить к выбранным. Рамкой: выбрать несколько. Двойной щелчок по фигуре: точный размер.',
  rect: 'Тяните мышью, чтобы нарисовать прямоугольник. Ctrl: квадрат. Shift: от центра.',
  ellipse: 'Тяните мышью, чтобы нарисовать эллипс. Ctrl: круг. Shift: от центра.',
  poly: 'Щёлкайте точки. По первой точке: замкнуть контур. Двойной щелчок или Enter: закончить линией. Backspace: убрать точку. Shift: угол кратен 15°. Esc: отменить.',
  start: 'Щёлкните по точке контура (кружки): с неё нож начнёт рез. У незамкнутой линии: по одному из концов.',
  node: 'Щёлкните по контуру, потом по узлу. Shift: несколько узлов, рамкой: сразу много. Тяните узлы и ручки кривых. Двойной щелчок по линии: новый узел. Delete: удалить узлы.',
  scissors: 'Наведите на кусок контура и щёлкните: он удалится. Куски отделены пересечениями с другими контурами и выбранными узлами. Shift+щелчок: поставить свою точку разреза.',
};

export type VariantEditorViewProps = {
  variant: Object,
  doc: Object,
  refs: Array<Object>,
  initialXy: Array<number>,
  shifted: boolean,
  onSave: (params: Object) => Promise<Object>,
  mode?: 'edit' | 'create',
  corner?: { x: number, y: number },
  previewPortrait?: boolean,
};

export default function VariantEditorView({
  variant, doc, refs, initialXy, shifted, onSave, mode = 'edit', corner = DEFAULT_CORNER, previewPortrait,
}: VariantEditorViewProps) {
  const isCreate = mode === 'create';
  const [init] = useState(() => ({
    xy: initialXy, deleted: {}, extra: [], replaced: {}, selected: {}, history: [], future: [],
  }));
  const [state, dispatch] = useReducer(reducer, init);
  const [base, setBase] = useState(init); // последнее сохранённое
  const stateRef = useRef(state);
  stateRef.current = state;

  const [view, setView] = useState({ k: 1, ox: 0, oy: 0 });
  const viewRef = useRef(view);
  viewRef.current = view;
  const [size, setSize] = useState({ w: 800, h: 500 });
  const [drag, setDrag] = useState(null);
  const dragRef = useRef(null);
  const [cursor, setCursor] = useState(null);
  const [anchor, setAnchor] = useState([0.5, 0.5]);
  const [lock, setLock] = useState(() => {
    try { return localStorage.getItem(LOCK_KEY) === '1'; } catch (e) { return false; }
  });
  const [radius, setRadius] = useState(() => {
    try { return parseMm(localStorage.getItem(RADIUS_KEY) || '') || 2; } catch (e) { return 2; }
  });
  const [angle, setAngle] = useState('');
  const [saveOpen, setSaveOpen] = useState(false);
  const [shapeOpen, setShapeOpen] = useState(null); // { initial, edit } окна «Фигура по размеру»
  const [space, setSpace] = useState(false);
  const [tool, setTool] = useState('select');
  const [poly, setPoly] = useState(null); // точки недорисованной ломаной
  const polyRef = useRef(null);
  polyRef.current = poly;
  const [showDir, setShowDir] = useState(() => {
    try { return localStorage.getItem(DIRECTION_KEY) !== '0'; } catch (e) { return true; }
  });
  const [snapOn, setSnapOn] = useState(() => {
    try { return localStorage.getItem(SNAP_KEY) !== '0'; } catch (e) { return true; }
  });
  const guidesKey = GUIDES_KEY + ((variant.device && variant.device.id) || '');
  const [guides, setGuides] = useState(() => {
    try {
      const list = JSON.parse(localStorage.getItem(guidesKey) || '[]');
      return Array.isArray(list) ? list.filter(g => g && (g.axis === 'x' || g.axis === 'y') && Number.isFinite(g.v)) : [];
    } catch (e) {
      return [];
    }
  });
  const [guideDialog, setGuideDialog] = useState(null); // направляющая, у которой правят положение
  const [nodeSel, setNodeSel] = useState({ key: null, nodes: [] }); // узлы контура для инструмента «Узлы»
  const nodeSelRef = useRef(nodeSel);
  nodeSelRef.current = nodeSel;
  const [cornerDraft, setCornerDraft] = useState(null); // ползунок скругления: от чего считать
  const [hoverPiece, setHoverPiece] = useState(null); // кусок под ножницами
  const altRef = useRef(false);
  const [flash, setFlash] = useState(null);
  const flashTimer = useRef(null);
  const canvasRef = useRef(null);
  const fitted = useRef(false);

  // Короткое сообщение в строке состояния
  const say = text => {
    setFlash(text);
    clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlash(null), 5000);
  };
  useEffect(() => () => clearTimeout(flashTimer.current), []);

  // --- Геометрия ---
  const pairsOf = useMemo(() => pairIndex(refs), [refs]);

  // Команды контура по ключу, как они уйдут в файл
  const itemCmds = useCallback((s, key) => {
    const i = +key.slice(1);
    if (key[0] === 'x') return s.extra[i].cmds;
    return s.replaced[i] || contourCmds(doc, i, s.xy, pairsOf);
  }, [doc, pairsOf]);

  // Элементы на холсте: ключ, сегменты, габариты. m — временное преобразование
  // выбранных на время перетаскивания (без округления); overrides — контуры,
  // которые сейчас правятся узлами или ползунком: { ключ: команды }
  const itemsFor = useCallback((s, m, overrides) => {
    const items = [];
    const sel = s.selected;
    let xy = s.xy;
    if (m) {
      const selectedContours = {};
      Object.keys(sel).forEach(key => { if (key[0] === 'c') selectedContours[+key.slice(1)] = true; });
      xy = applyTransform(xy, refs, selectedContours, m, false);
    }
    doc.contours.forEach((_, i) => {
      if (s.deleted[i]) return;
      const key = `c${i}`;
      let segs;
      if (overrides && overrides[key]) segs = cmdsSegments(overrides[key]);
      else if (s.replaced[i]) segs = cmdsSegments(m && sel[key] ? transformCmds(s.replaced[i], m, false) : s.replaced[i]);
      else segs = contourSegments(doc, i, xy, pairsOf);
      if (segs.length) items.push({ key, segs, box: segmentsBox(segs) });
    });
    s.extra.forEach((x, k) => {
      const key = `x${k}`;
      let cmds = x.cmds;
      if (overrides && overrides[key]) {
        cmds = overrides[key];
      } else if (m && sel[key]) {
        const moved = x.shape && transformShape(x.shape, m);
        cmds = moved ? shapeCmds(moved) : transformCmds(x.cmds, m, false);
      }
      const segs = cmdsSegments(cmds);
      if (segs.length) items.push({ key, segs, box: segmentsBox(segs) });
    });
    return items;
  }, [doc, refs, pairsOf]);

  const previewOverrides = (drag && drag.overrides)
    || (cornerDraft && cornerDraft.preview ? { [cornerDraft.key]: cornerDraft.preview } : null);
  const items = useMemo(() => itemsFor(state, drag && drag.m, previewOverrides), [state, drag, itemsFor, cornerDraft]);
  const allBox = useMemo(() => unionOf(items), [items]);
  const selItems = items.filter(it => state.selected[it.key]);
  const selBox = selItems.length ? unionOf(selItems) : null;
  const originalBox = useMemo(() => unionOf(itemsFor(init)), [init, itemsFor]);
  const savedBox = useMemo(() => unionOf(itemsFor(state)), [state, itemsFor]);
  // Куда встаёт лекало: новое — в угол листа, как остальные лекала модели;
  // правленое — туда же, где было
  const cornerTarget = isCreate || !boxOk(originalBox)
    ? corner
    : { x: Math.round(originalBox.minX), y: Math.round(originalBox.minY) };
  const saveShift = box => {
    if (!isCreate) return placementShift(box, originalBox);
    return boxOk(box) ? [Math.round(corner.x - box.minX), Math.round(corner.y - box.minY)] : [0, 0];
  };
  const shift = saveShift(savedBox);
  // Есть правки после открытия или последнего сохранения. Снимки неизменяемые:
  // отмена до сохранённого возвращает те же объекты, и правок снова нет
  const dirty = state.xy !== base.xy || state.deleted !== base.deleted
    || state.extra !== base.extra || state.replaced !== base.replaced;

  const sheet = useMemo(() => {
    if (doc.sheet && doc.sheet.w > 0 && doc.sheet.h > 0) {
      return shifted ? { w: doc.sheet.h, h: doc.sheet.w } : doc.sheet;
    }
    return null;
  }, [doc, shifted]);
  // Пустое лекало показывается листом, чтобы был виден угол, куда оно встанет
  const sheetBox = sheet ? { minX: 0, minY: 0, maxX: sheet.w, maxY: sheet.h } : { minX: 0, minY: 0, maxX: 2000, maxY: 1200 };
  const fitAll = () => fitTo(boxOk(allBox) ? allBox : sheetBox);

  // Новый контур после преобразования: фигура остаётся фигурой, пока её
  // не перекосило (растяжение повёрнутой), иначе становится обычным контуром
  const movedNew = (x, m) => {
    const shape = x.shape && transformShape(x.shape, m);
    return shape ? { cmds: shapeCmds(shape), shape } : { cmds: transformCmds(x.cmds, m, true) };
  };

  // Выбранные ключи → новое состояние с округлённым преобразованием
  const transformed = useCallback((s, m, keys) => {
    const selectedContours = {};
    const replaced = { ...s.replaced };
    Object.keys(keys).forEach(key => {
      if (key[0] !== 'c') return;
      const i = +key.slice(1);
      if (replaced[i]) replaced[i] = transformCmds(replaced[i], m, true);
      else selectedContours[i] = true;
    });
    const xy = applyTransform(s.xy, refs, selectedContours, m, true);
    const extra = s.extra.map((x, k) => (keys[`x${k}`] ? movedNew(x, m) : x));
    return { xy, extra, replaced };
  }, [refs]);

  const commitTransform = useCallback(m => {
    const s = stateRef.current;
    if (!Object.keys(s.selected).length) return;
    dispatch({ type: 'commit', ...transformed(s, m, s.selected) });
  }, [transformed]);

  const anchorOf = box => anchorPoint(box, anchor[0], anchor[1]);
  const currentSelBox = () => {
    const s = stateRef.current;
    const picked = itemsFor(s).filter(it => s.selected[it.key]);
    return picked.length ? unionOf(picked) : null;
  };

  // Выбрана одна фигура: у неё точный размер и радиус углов
  const singleShape = (() => {
    const keys = Object.keys(state.selected);
    if (keys.length !== 1 || keys[0][0] !== 'x') return null;
    const k = +keys[0].slice(1);
    const x = state.extra[k];
    return x && x.shape ? { k, shape: x.shape } : null;
  })();
  const singleRect = singleShape && singleShape.shape.type === 'rect' ? singleShape : null;

  // --- Форма контуров: узлы, куски, пересечения, привязка ---
  // Контуры как цепочки отрезков; пересчитываются только после правки
  const geo = useMemo(() => ({ paths: new Map(), cuts: new Map(), nodes: null, boxes: new Map() }), [state]);
  const allKeys = () => {
    const s = stateRef.current;
    const keys = [];
    doc.contours.forEach((_, i) => { if (!s.deleted[i]) keys.push(`c${i}`); });
    s.extra.forEach((_, k) => keys.push(`x${k}`));
    return keys;
  };
  const pathOf = key => {
    if (!geo.paths.has(key)) geo.paths.set(key, pathFromCmds(itemCmds(stateRef.current, key)));
    return geo.paths.get(key);
  };
  // Габариты по всем точкам, с ручками: грубо, для отсева
  const roughBox = key => {
    if (!geo.boxes.has(key)) {
      const b = emptyBox();
      pathOf(key).parts.forEach(pt => [pt.p0, pt.c1, pt.c2, pt.p1].forEach(q => {
        if (!q) return;
        b.minX = Math.min(b.minX, q[0]); b.maxX = Math.max(b.maxX, q[0]);
        b.minY = Math.min(b.minY, q[1]); b.maxY = Math.max(b.maxY, q[1]);
      }));
      geo.boxes.set(key, b);
    }
    return geo.boxes.get(key);
  };
  const meets = (a, b, pad = 0) => a.minX <= b.maxX + pad && b.minX <= a.maxX + pad && a.minY <= b.maxY + pad && b.minY <= a.maxY + pad;
  const keyExists = key => {
    if (!key) return false;
    const s = stateRef.current;
    const i = +key.slice(1);
    return key[0] === 'c' ? i < doc.contours.length && !s.deleted[i] : i < s.extra.length;
  };

  // Где режут ножницы: пересечения с другими контурами и выбранные узлы
  const cutsFor = key => {
    const ns = nodeSelRef.current;
    const cacheKey = `${key}|${ns.key === key ? ns.nodes.join(',') : ''}`;
    if (geo.cuts.has(cacheKey)) return geo.cuts.get(cacheKey);
    const path = pathOf(key);
    const cuts = [];
    allKeys().forEach(other => {
      if (other === key || !meets(roughBox(key), roughBox(other))) return;
      intersectPaths(path, pathOf(other)).forEach(h => cuts.push({ g: h.ga, p: h.p }));
    });
    if (ns.key === key) ns.nodes.forEach(k => cuts.push({ g: k }));
    geo.cuts.set(cacheKey, cuts);
    return cuts;
  };

  // Привязка: узлы всех контуров — точно, направляющие и края листа — по осям
  const snapNodes = () => {
    if (!geo.nodes) {
      const list = [];
      allKeys().forEach(key => pathNodes(pathOf(key)).forEach((q, k) => list.push({ key, k, p: q })));
      geo.nodes = list;
    }
    return geo.nodes;
  };
  const snapping = () => snapOn && !altRef.current;
  const axisValues = axis => guides.filter(g => g.axis === axis).map(g => g.v).concat([0]);
  const snapAxes = q => {
    const tol = SNAP_PX / viewRef.current.k;
    const out = q.slice();
    const marks = [];
    ['x', 'y'].forEach((axis, i) => {
      let best = null;
      axisValues(axis).forEach(v => {
        if (Math.abs(v - q[i]) <= tol && (best === null || Math.abs(v - q[i]) < Math.abs(best - q[i]))) best = v;
      });
      if (best !== null) {
        out[i] = best;
        marks.push({ type: axis, v: best });
      }
    });
    return { q: out, marks };
  };
  const snapPoint = (q, skip) => {
    if (!snapping()) return { q, marks: [] };
    const tol = SNAP_PX / viewRef.current.k;
    let best = null;
    snapNodes().forEach(t => {
      if (skip && skip(t)) return;
      const d = dist(t.p, q);
      if (d <= tol && (!best || d < best.d)) best = { d, p: t.p };
    });
    if (best) return { q: best.p.slice(), marks: [{ type: 'point', p: best.p }] };
    return snapAxes(q);
  };
  // Сдвиг выбранного: узел, за который взяли, — к узлам других контуров;
  // края и середина рамки — к направляющим и краю листа
  const snapMove = (d, dx, dy) => {
    if (!snapping()) return { dx, dy, marks: [] };
    const tol = SNAP_PX / viewRef.current.k;
    if (d.grab) {
      const q = [d.grab[0] + dx, d.grab[1] + dy];
      let best = null;
      snapNodes().forEach(t => {
        if (d.moving[t.key]) return;
        const dd = dist(t.p, q);
        if (dd <= tol && (!best || dd < best.d)) best = { d: dd, p: t.p };
      });
      if (best) return { dx: best.p[0] - d.grab[0], dy: best.p[1] - d.grab[1], marks: [{ type: 'point', p: best.p }] };
    }
    const b = d.box;
    const marks = [];
    const fit = (axis, vals, delta) => {
      let best = null;
      axisValues(axis).forEach(v => vals.forEach(src => {
        const dd = v - (src + delta);
        if (Math.abs(dd) <= tol && (best === null || Math.abs(dd) < Math.abs(best.dd))) best = { dd, v };
      }));
      if (!best) return delta;
      marks.push({ type: axis, v: best.v });
      return delta + best.dd;
    };
    return {
      dx: fit('x', [b.minX, (b.minX + b.maxX) / 2, b.maxX], dx),
      dy: fit('y', [b.minY, (b.minY + b.maxY) / 2, b.maxY], dy),
      marks,
    };
  };
  // Узел выбранных контуров под мышью (до 10 px) — за него «взяли»
  const grabNode = (q, moving) => {
    let best = null;
    const tol = 10 / viewRef.current.k;
    snapNodes().forEach(t => {
      if (!moving[t.key]) return;
      const d = dist(t.p, q);
      if (d <= tol && (!best || d < best.d)) best = { d, p: t.p };
    });
    return best ? best.p : null;
  };

  // Правка формы: set — новые команды контуров, remove — убрать контуры,
  // add — новые контуры. Номера новых («x…») после удаления сдвигаются;
  // select — прежние ключи того, что выбрать после правки
  const commitEdit = ({ set = {}, remove = [], add = [], select = [], selectAdded = false }) => {
    const s = stateRef.current;
    const deleted = { ...s.deleted };
    const replaced = { ...s.replaced };
    const extra = s.extra.slice();
    Object.keys(set).forEach(key => {
      const i = +key.slice(1);
      if (key[0] === 'c') replaced[i] = set[key];
      else extra[i] = { cmds: set[key] };
    });
    const dropX = {};
    remove.forEach(key => {
      if (key[0] === 'c') deleted[+key.slice(1)] = true;
      else dropX[+key.slice(1)] = true;
    });
    const map = {};
    const kept = [];
    extra.forEach((x, i) => {
      if (!dropX[i]) {
        map[i] = kept.length;
        kept.push(x);
      }
    });
    const rekey = key => {
      if (key[0] === 'c') return deleted[+key.slice(1)] ? null : key;
      return map[+key.slice(1)] !== undefined ? `x${map[+key.slice(1)]}` : null;
    };
    const selected = {};
    select.forEach(k => { const r = rekey(k); if (r) selected[r] = true; });
    if (selectAdded) add.forEach((_, j) => { selected[`x${kept.length + j}`] = true; });
    dispatch({ type: 'commit', deleted, replaced, extra: kept.concat(add.map(cmds => ({ cmds }))), selected });
    return rekey;
  };

  // --- Узлы ---
  const activeKey = tool === 'node' && keyExists(nodeSel.key) ? nodeSel.key : null;
  const activePath = activeKey ? pathOf(activeKey) : null;
  const commitPath = (key, path, nodes = []) => {
    commitEdit({ set: { [key]: pathToCmds(path) }, select: [key] });
    setNodeSel({ key, nodes });
    setCornerDraft(null);
  };
  const moveSelectedNodes = d => {
    const ns = nodeSelRef.current;
    if (!keyExists(ns.key) || !ns.nodes.length) return;
    commitPath(ns.key, moveNodes(pathOf(ns.key), ns.nodes, d), ns.nodes);
  };
  const setNodePos = (axis, mmVal) => {
    const ns = nodeSelRef.current;
    if (!keyExists(ns.key) || ns.nodes.length !== 1) return;
    const q = pathNodes(pathOf(ns.key))[ns.nodes[0]];
    const target = Math.round(mmVal * UNITS_PER_MM);
    moveSelectedNodes(axis === 0 ? [target - q[0], 0] : [0, target - q[1]]);
  };
  const deleteSelectedNodes = () => {
    const ns = nodeSelRef.current;
    if (!keyExists(ns.key) || !ns.nodes.length) return false;
    const next = deleteNodes(pathOf(ns.key), ns.nodes);
    if (!next) {
      say('Столько узлов не удалить: от контура ничего не останется. Удалить весь контур: инструмент выбора и Delete.');
      return true;
    }
    commitPath(ns.key, next, []);
    say(`Удалено узлов: ${ns.nodes.length}.`);
    return true;
  };
  // Отрезок, что выходит из узла (у конца открытой линии — что входит)
  const partOfNode = (path, k) => (k < path.parts.length ? k : k - 1);
  const nodesToType = type => {
    const ns = nodeSelRef.current;
    if (!keyExists(ns.key) || !ns.nodes.length) return;
    let path = pathOf(ns.key);
    let changed = 0;
    ns.nodes.forEach(k => {
      const i = partOfNode(path, k);
      if (path.parts[i] && path.parts[i].type !== type) {
        path = setPartType(path, i, type);
        changed += 1;
      }
    });
    if (!changed) {
      say(type === 'C' ? 'Отрезки у выбранных узлов уже кривые.' : 'Отрезки у выбранных узлов уже прямые.');
      return;
    }
    commitPath(ns.key, path, ns.nodes);
    say(type === 'C' ? 'Отрезки стали кривыми: тяните ручки, чтобы выгнуть.' : 'Отрезки стали прямыми.');
  };
  // Новые узлы посередине отрезков после выбранных узлов
  const addNodesMid = () => {
    const ns = nodeSelRef.current;
    if (!keyExists(ns.key) || !ns.nodes.length) return;
    let path = pathOf(ns.key);
    const added = [];
    ns.nodes.slice().sort((a, b) => b - a).forEach(k => {
      const res = insertNode(path, partOfNode(path, k) + 0.5);
      path = res.path;
      added.forEach((n, j) => { if (n >= res.node) added[j] = n + 1; });
      added.push(res.node);
    });
    commitPath(ns.key, path, added);
  };
  const insertNodeAt = q => {
    const key = nodeSelRef.current.key;
    if (!keyExists(key)) return;
    const near = nearestOnPath(pathOf(key), q);
    if (!near || near.d * viewRef.current.k > 8) return;
    const res = insertNode(pathOf(key), near.i + near.t);
    commitPath(key, res.path, [res.node]);
  };

  // Ползунок скругления углов: радиус считается от контура до скругления,
  // пока не выбраны другие узлы — двигать можно сколько угодно раз
  const cornerDraftRef = useRef(cornerDraft);
  cornerDraftRef.current = cornerDraft;
  const cornerNodes = () => (activePath ? nodeSel.nodes.filter(k => cornerAt(activePath, k)) : []);
  const draftFor = () => {
    const d = cornerDraftRef.current;
    if (d && d.key === nodeSelRef.current.key) return d;
    const key = nodeSelRef.current.key;
    if (!keyExists(key)) return null;
    const path = pathOf(key);
    const corners = nodeSelRef.current.nodes.filter(k => cornerAt(path, k));
    if (!corners.length) return null;
    return { key, base: path, corners, maxR: Math.min(...corners.map(k => cornerAt(path, k).maxR)), r: 0, preview: null };
  };
  const cornerCmds = (d, r) => pathToCmds(r > 0 ? filletNodes(d.base, d.corners, r) : d.base);
  const previewCorner = rMm => {
    const d = draftFor();
    if (!d) return;
    const r = Math.max(0, Math.min(rMm * UNITS_PER_MM, d.maxR));
    const next = { ...d, r, preview: cornerCmds(d, r) };
    cornerDraftRef.current = next;
    setCornerDraft(next);
  };
  const commitCorner = () => {
    const d = cornerDraftRef.current;
    if (!d || !d.preview) return;
    commitEdit({ set: { [d.key]: d.preview }, select: [d.key] });
    const next = { ...d, preview: null, committed: d.preview };
    cornerDraftRef.current = next;
    setCornerDraft(next);
    setNodeSel({ key: d.key, nodes: [] });
    say(d.r > 0 ? `Углов скруглено: ${d.corners.length}, радиус ${formatMm(d.r)} мм. Ползунком можно поправить.` : 'Скругление снято.');
  };
  const applyCorner = rMm => {
    previewCorner(rMm);
    commitCorner();
  };
  // Другая правка или отмена — ползунок начинает с нового контура
  useEffect(() => {
    const d = cornerDraftRef.current;
    if (!d || d.preview || !d.committed) return;
    const i = +d.key.slice(1);
    const cur = d.key[0] === 'c' ? state.replaced[i] : state.extra[i] && state.extra[i].cmds;
    if (cur !== d.committed) setCornerDraft(null);
  }, [state]);

  // --- Ножницы: кусок контура между разрезами ---
  const pieceAt = p => {
    const tol = 8 / viewRef.current.k;
    let best = null;
    allKeys().forEach(key => {
      const b = roughBox(key);
      if (p.x < b.minX - tol || p.x > b.maxX + tol || p.y < b.minY - tol || p.y > b.maxY + tol) return;
      const path = pathOf(key);
      if (!path.parts.length) return;
      const near = nearestOnPath(path, [p.x, p.y]);
      if (near && near.d <= tol && (!best || near.d < best.d)) best = { key, path, d: near.d };
    });
    if (!best) return null;
    const pieces = splitPathAt(best.path, cutsFor(best.key));
    let idx = 0;
    let dmin = Infinity;
    pieces.forEach((pc, j) => {
      const near = nearestOnPath(pc, [p.x, p.y]);
      if (near && near.d < dmin) { dmin = near.d; idx = j; }
    });
    return { key: best.key, closed: best.path.closed, pieces, idx };
  };
  const deletePiece = h => {
    const remaining = withoutPiece(h.pieces, h.idx, h.closed);
    const removed = h.pieces[h.idx];
    const seeds = [removed.parts[0].p0, removed.parts[removed.parts.length - 1].p1];
    // куски этого контура и открытые линии, чьи концы там, где резали
    const entries = remaining.map((path, j) => ({ path, keys: j === 0 ? [h.key] : [`+${j}`] }));
    allKeys().forEach(key => {
      if (key === h.key) return;
      const path = pathOf(key);
      if (path.closed || !path.parts.length) return;
      const ends = [path.parts[0].p0, path.parts[path.parts.length - 1].p1];
      if (ends.some(e => seeds.some(q => dist(e, q) <= 1))) entries.push({ path, keys: [key] });
    });
    const joined = joinAt(entries, seeds, 1);
    const set = {};
    const remove = [];
    const add = [];
    let owner = null;
    let glued = false;
    joined.forEach(e => {
      const real = e.keys.filter(k => k[0] !== '+');
      if (e.keys.length > 1) glued = true;
      const cmds = pathToCmds(e.path);
      if (!real.length) {
        add.push(cmds);
        return;
      }
      const main = real.includes(h.key) ? h.key : real[0];
      set[main] = cmds;
      real.filter(k => k !== main).forEach(k => remove.push(k));
      if (main === h.key) owner = h.key;
    });
    if (!owner) remove.push(h.key);
    commitEdit({ set, remove, add, select: owner ? [owner] : [], selectAdded: !owner });
    setNodeSel({ key: null, nodes: [] });
    setHoverPiece(null);
    const closedNow = joined.some(e => e.path.closed && e.keys.length > 1);
    say(`Кусок удалён.${glued ? ' Концы сошлись: куски соединены' : ''}${closedNow ? ' в замкнутый контур.' : (glued ? '.' : '')}`);
  };
  // Shift+щелчок ножницами: своя точка разреза на контуре
  const addCutPoint = p => {
    const tol = 8 / viewRef.current.k;
    let best = null;
    allKeys().forEach(key => {
      const path = pathOf(key);
      if (!path.parts.length) return;
      const near = nearestOnPath(path, [p.x, p.y]);
      if (near && near.d <= tol && (!best || near.d < best.near.d)) best = { key, path, near };
    });
    if (!best) {
      say('Щёлкните с Shift прямо по контуру: там встанет точка разреза.');
      return;
    }
    const res = insertNode(best.path, best.near.i + best.near.t);
    const ns = nodeSelRef.current;
    const prev = ns.key === best.key ? ns.nodes.map(k => (res.path !== best.path && k >= res.node ? k + 1 : k)) : [];
    commitEdit({ set: { [best.key]: pathToCmds(res.path) }, select: [best.key] });
    setNodeSel({ key: best.key, nodes: prev.filter(k => k !== res.node).concat([res.node]) });
    setHoverPiece(null);
    say('Точка разреза поставлена. Поставьте вторую (Shift+щелчок) или щёлкните по куску, чтобы удалить его.');
  };

  // --- Объединить, вычесть, пересечь ---
  const closedKeys = Object.keys(state.selected).filter(key => keyExists(key) && pathFromCmds(itemCmds(state, key), 5).closed);
  const canBoolean = Object.keys(state.selected).length === 2 && closedKeys.length === 2;
  const doBoolean = op => {
    const s = stateRef.current;
    const keys = Object.keys(s.selected);
    if (keys.length !== 2) {
      say('Выберите два замкнутых контура.');
      return;
    }
    const paths = keys.map(key => pathFromCmds(itemCmds(s, key), 5));
    if (!paths.every(pth => pth.closed)) {
      say('Оба контура должны быть замкнутыми.');
      return;
    }
    // вычитается меньший из большего
    const all = itemsFor(s);
    const area = key => boxArea(unionOf(all.filter(it => it.key === key)));
    const [a, b] = area(keys[0]) >= area(keys[1]) ? [0, 1] : [1, 0];
    const res = booleanPaths(paths[a], paths[b], op);
    if (!res.length) {
      say(op === 'intersection' ? 'У контуров нет общей части.' : 'После вычитания ничего не осталось.');
      return;
    }
    commitEdit({
      set: { [keys[a]]: pathToCmds(res[0]) },
      remove: [keys[b]],
      add: res.slice(1).map(pathToCmds),
      select: [keys[a]],
      selectAdded: true,
    });
    const verb = { union: 'Объединено', difference: 'Вычтено (из большего контура меньший)', intersection: 'Оставлена общая часть' }[op];
    say(`${verb}: контуров ${res.length}.`);
  };

  // --- Направляющие ---
  const saveGuides = list => {
    setGuides(list);
    try { localStorage.setItem(guidesKey, JSON.stringify(list)); } catch (e) { /* без памяти */ }
  };
  // Направляющая липнет к узлам контуров по своей оси
  const snapGuide = (axis, v) => {
    if (!snapping()) return { v: Math.round(v), marks: [] };
    const tol = SNAP_PX / viewRef.current.k;
    const i = axis === 'x' ? 0 : 1;
    let best = null;
    snapNodes().forEach(t => {
      const d = Math.abs(t.p[i] - v);
      if (d <= tol && (!best || d < best.d)) best = { d, p: t.p };
    });
    return best ? { v: Math.round(best.p[i]), marks: [{ type: 'point', p: best.p }] } : { v: Math.round(v), marks: [] };
  };

  // --- Действия ---
  const addContours = (list, message) => {
    const s = stateRef.current;
    const selected = {};
    list.forEach((_, j) => { selected[`x${s.extra.length + j}`] = true; });
    dispatch({ type: 'commit', extra: s.extra.concat(list), selected });
    if (message) say(message);
  };
  const selectAll = () => {
    const keys = {};
    itemsFor(stateRef.current).forEach(it => { keys[it.key] = true; });
    dispatch({ type: 'select', keys });
  };
  const removeSelected = () => {
    const s = stateRef.current;
    const keys = Object.keys(s.selected);
    if (!keys.length) return;
    const deleted = { ...s.deleted };
    keys.forEach(key => { if (key[0] === 'c') deleted[+key.slice(1)] = true; });
    const extra = s.extra.filter((_, k) => !s.selected[`x${k}`]);
    dispatch({ type: 'commit', deleted, extra, selected: {} });
  };
  // Выбранное как новые контуры, в порядке реза
  const selectedAsNew = s => itemsFor(s).filter(it => s.selected[it.key]).map(it => {
    if (it.key[0] === 'x') {
      const x = s.extra[+it.key.slice(1)];
      return x.shape ? { cmds: x.cmds, shape: x.shape } : { cmds: x.cmds };
    }
    return { cmds: itemCmds(s, it.key) };
  });
  // Копия со сдвигом 5 мм вправо и вниз, как «Дублировать» в CorelDRAW
  const duplicateSelected = () => {
    const list = selectedAsNew(stateRef.current);
    const m = translate(5 * UNITS_PER_MM, -5 * UNITS_PER_MM);
    if (list.length) addContours(list.map(x => movedNew(x, m)));
  };
  const copySelected = cut => {
    const list = selectedAsNew(stateRef.current);
    if (!list.length) return;
    try {
      localStorage.setItem(CLIPBOARD_KEY, JSON.stringify({ items: list, from: variant.name, at: Date.now() }));
    } catch (e) {
      say('Не получилось скопировать: браузер не дал сохранить данные.');
      return;
    }
    if (cut) removeSelected();
    say(`${cut ? 'Вырезано' : 'Скопировано'} контуров: ${list.length}. Вставить: Ctrl+V, можно и в другом лекале.`);
  };
  const paste = () => {
    const data = readClipboard();
    if (!data) {
      say('Вставлять нечего: сначала скопируйте контуры (Ctrl+C).');
      return;
    }
    const from = data.from && data.from !== variant.name ? ` из «${data.from}»` : '';
    addContours(data.items, `Вставлено контуров${from}: ${data.items.length}, на то же место. Они выбраны, их можно двигать.`);
  };
  const moveBy = (dx, dy) => commitTransform(translate(dx, dy));
  const setAnchorPos = (axis, mmVal) => {
    const box = currentSelBox();
    if (!box) return;
    const [ax, ay] = anchorOf(box);
    const target = Math.round(mmVal * UNITS_PER_MM);
    commitTransform(axis === 0 ? translate(target - ax, 0) : translate(0, target - ay));
  };
  const resizeTo = (axis, mmVal) => {
    const box = currentSelBox();
    if (!box || mmVal <= 0) return;
    const w = box.maxX - box.minX;
    const h = box.maxY - box.minY;
    const cur = axis === 0 ? w : h;
    if (cur <= 0) return;
    const s = (mmVal * UNITS_PER_MM) / cur;
    const [ax, ay] = anchorOf(box);
    commitTransform(lock ? scaleAbout(s, s, ax, ay) : axis === 0 ? scaleAbout(s, 1, ax, ay) : scaleAbout(1, s, ax, ay));
  };
  const rotate = deg => {
    const box = currentSelBox();
    if (!box || !deg) return;
    const [ax, ay] = anchorOf(box);
    commitTransform(rotateAbout(deg, ax, ay));
  };
  const mirror = axis => {
    const box = currentSelBox();
    if (!box) return;
    const [ax, ay] = anchorOf(box);
    commitTransform(axis === 0 ? scaleAbout(-1, 1, ax, ay) : scaleAbout(1, -1, ax, ay));
  };
  // По центру самого большого невыбранного контура: обычно это внешний
  // контур лекала, а выбранное — вырез под камеру или кнопку
  const alignCenter = axis => {
    const s = stateRef.current;
    const all = itemsFor(s);
    const picked = all.filter(it => s.selected[it.key]);
    const others = all.filter(it => !s.selected[it.key]);
    if (!picked.length) return;
    if (!others.length) {
      say('Выбрано всё: не по чему ставить по центру. Выберите только то, что двигать.');
      return;
    }
    const ref = biggest(others).box;
    const sb = unionOf(picked);
    const d = axis === 0
      ? (ref.minX + ref.maxX) / 2 - (sb.minX + sb.maxX) / 2
      : (ref.minY + ref.maxY) / 2 - (sb.minY + sb.maxY) / 2;
    commitTransform(axis === 0 ? translate(Math.round(d), 0) : translate(0, Math.round(d)));
    say(`По центру контура ${mmText(ref.maxX - ref.minX)} × ${mmText(ref.maxY - ref.minY)} мм.`);
  };
  const rememberRadius = mmVal => {
    setRadius(mmVal);
    try { localStorage.setItem(RADIUS_KEY, String(mmVal)); } catch (e) { /* без памяти */ }
  };
  // Закругление: у прямоугольника-фигуры меняется её радиус углов, у
  // остальных контуров острые углы между прямыми заменяются дугами
  const roundCorners = mmVal => {
    const R = Math.round(Math.max(0, mmVal) * UNITS_PER_MM);
    const s = stateRef.current;
    const keys = Object.keys(s.selected);
    if (!keys.length) return;
    let corners = 0;
    let rects = 0;
    const extra = s.extra.slice();
    const replaced = { ...s.replaced };
    keys.forEach(key => {
      const i = +key.slice(1);
      if (key[0] === 'x') {
        const x = extra[i];
        if (x.shape && x.shape.type === 'rect') {
          const shape = { ...x.shape, r: Math.min(R, x.shape.w / 2, x.shape.h / 2) };
          extra[i] = { cmds: shapeCmds(shape), shape };
          rects += 1;
        } else if (!x.shape) {
          const f = filletCmds(x.cmds, R);
          if (f.count) { extra[i] = { cmds: f.cmds }; corners += f.count; }
        }
      } else {
        const f = filletCmds(itemCmds(s, key), R);
        if (f.count) { replaced[i] = f.cmds; corners += f.count; }
      }
    });
    if (!corners && !rects) {
      say(R ? 'У выбранного нет острых углов между прямыми линиями.' : 'Радиус 0: скруглять нечего.');
      return;
    }
    dispatch({ type: 'commit', extra, replaced });
    if (corners) say(`Скруглено углов: ${corners}, радиус ${formatMm(R)} мм. Отменить: Ctrl+Z.`);
  };

  // --- Фигура по размеру ---
  const openShapeDialog = edit => {
    if (edit) {
      setShapeOpen({ initial: edit.shape, edit: edit.k });
      return;
    }
    const all = itemsFor(stateRef.current);
    const v = viewRef.current;
    let cx = (size.w / 2 - v.ox) / v.k;
    let cy = (v.oy - size.h / 2) / v.k;
    if (all.length) {
      const b = biggest(all).box;
      cx = (b.minX + b.maxX) / 2;
      cy = (b.minY + b.maxY) / 2;
    }
    setShapeOpen({ initial: { type: 'rect', cx: Math.round(cx), cy: Math.round(cy), w: 200, h: 100, r: 0, angle: 0 }, edit: null });
  };
  const applyShape = shape => {
    const edit = shapeOpen && shapeOpen.edit;
    setShapeOpen(null);
    setTool('select');
    const item = { cmds: shapeCmds(shape), shape };
    const name = shape.type === 'rect' ? 'Прямоугольник' : 'Овал';
    if (edit === null || edit === undefined) {
      addContours([item], `${name} ${mmText(shape.w)} × ${mmText(shape.h)} мм добавлен и выбран.`);
      return;
    }
    const s = stateRef.current;
    const extra = s.extra.slice();
    const prev = extra[edit].shape;
    if (prev && prev.reverse) {
      const kept = { ...shape, reverse: true };
      extra[edit] = { cmds: shapeCmds(kept), shape: kept };
    } else {
      extra[edit] = item;
    }
    dispatch({ type: 'commit', extra, selected: { [`x${edit}`]: true } });
  };

  // --- Начало и направление реза ---
  const rememberDir = on => {
    setShowDir(on);
    try { localStorage.setItem(DIRECTION_KEY, on ? '1' : '0'); } catch (e) { /* без памяти */ }
  };
  const reverseSelected = () => {
    const s = stateRef.current;
    const keys = Object.keys(s.selected);
    if (!keys.length) return;
    const extra = s.extra.slice();
    const replaced = { ...s.replaced };
    keys.forEach(key => {
      const i = +key.slice(1);
      if (key[0] === 'x') {
        const x = extra[i];
        if (x.shape) {
          const shape = { ...x.shape, reverse: !x.shape.reverse };
          extra[i] = { cmds: shapeCmds(shape), shape };
        } else {
          extra[i] = { cmds: reverseCmds(x.cmds) };
        }
      } else {
        replaced[i] = reverseCmds(itemCmds(s, key));
      }
    });
    dispatch({ type: 'commit', extra, replaced });
    if (!showDir) rememberDir(true);
    say(`Направление реза обращено, контуров: ${keys.length}.`);
  };
  // Узел контура под мышью (до 10 px): с него начнётся рез
  const startTarget = p => {
    const s = stateRef.current;
    const v = viewRef.current;
    let best = null;
    itemsFor(s).forEach(it => {
      const { closed, points } = cmdsVertices(itemCmds(s, it.key));
      points.forEach((q, k) => {
        const d = Math.hypot(v.ox + q[0] * v.k - p.sx, v.oy - q[1] * v.k - p.sy);
        if (d <= 10 && (!best || d < best.d)) best = { key: it.key, k, closed, d };
      });
    });
    return best;
  };
  const setStart = t => {
    const s = stateRef.current;
    const i = +t.key.slice(1);
    if (t.k === 0) {
      dispatch({ type: 'select', keys: { [t.key]: true } });
      say('Рез этого контура и так начинается здесь.');
      return;
    }
    const cmds = itemCmds(s, t.key);
    // у замкнутого — с выбранного узла, у линии — с другого конца
    const next = t.closed ? restartCmds(cmds, t.k) : reverseCmds(cmds);
    const extra = s.extra.slice();
    const replaced = { ...s.replaced };
    if (t.key[0] === 'x') extra[i] = { cmds: next }; else replaced[i] = next;
    dispatch({ type: 'commit', extra, replaced, selected: { [t.key]: true } });
    say('Начало реза перенесено: нож начнёт этот контур с выбранной точки.');
  };
  // Всё лекало — в угол листа
  const toCorner = () => {
    const s = stateRef.current;
    const all = itemsFor(s);
    if (!all.length) return;
    const b = unionOf(all);
    const dx = Math.round(cornerTarget.x - b.minX);
    const dy = Math.round(cornerTarget.y - b.minY);
    if (!dx && !dy) {
      say('Лекало уже в углу листа.');
      return;
    }
    const keys = {};
    all.forEach(it => { keys[it.key] = true; });
    dispatch({ type: 'commit', ...transformed(s, translate(dx, dy), keys) });
    say(`Лекало в углу листа: X ${formatMm(cornerTarget.x)}, Y ${formatMm(cornerTarget.y)} мм.`);
  };

  // --- Вид ---
  const fitTo = useCallback((box, w = size.w, h = size.h) => {
    if (!boxOk(box)) return;
    const bw = Math.max(box.maxX - box.minX, 10);
    const bh = Math.max(box.maxY - box.minY, 10);
    const k = Math.min((w * 0.84) / bw, (h * 0.84) / bh);
    setView({ k, ox: w / 2 - ((box.minX + box.maxX) / 2) * k, oy: h / 2 + ((box.minY + box.maxY) / 2) * k });
  }, [size]);

  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return undefined;
    const measure = () => {
      const r = el.getBoundingClientRect();
      setSize({ w: r.width, h: r.height });
      if (!fitted.current && r.width > 0) {
        fitted.current = true;
        fitTo(boxOk(allBox) ? allBox : sheetBox, r.width, r.height);
      }
    };
    measure();
    if (window.ResizeObserver) {
      const ro = new window.ResizeObserver(measure);
      ro.observe(el.parentNode);
      return () => ro.disconnect();
    }
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const zoomAt = (factor, sx, sy) => {
    const v = viewRef.current;
    const k = Math.min(80, Math.max(0.02, v.k * factor));
    const x = (sx - v.ox) / v.k;
    const y = (v.oy - sy) / v.k;
    setView({ k, ox: sx - x * k, oy: sy + y * k });
  };

  const toUnits = e => {
    const r = canvasRef.current.getBoundingClientRect();
    const v = viewRef.current;
    const sx = e.clientX - r.left;
    const sy = e.clientY - r.top;
    return { sx, sy, x: (sx - v.ox) / v.k, y: (v.oy - sy) / v.k };
  };

  // --- Рисование ---
  // Точка ломаной в целых единицах; с Shift угол от предыдущей точки кратен 15°
  const polyPoint = (p, shiftKey) => {
    const pts = polyRef.current;
    if (shiftKey && pts && pts.length) {
      const [lx, ly] = pts[pts.length - 1];
      const dist = Math.hypot(p.x - lx, p.y - ly);
      const step = Math.PI / 12;
      const a = Math.round(Math.atan2(p.y - ly, p.x - lx) / step) * step;
      return [Math.round(lx + dist * Math.cos(a)), Math.round(ly + dist * Math.sin(a))];
    }
    const q = snapPoint([p.x, p.y]).q;
    return [Math.round(q[0]), Math.round(q[1])];
  };
  const finishPoly = closed => {
    const pts = polyRef.current;
    setPoly(null);
    if (!pts || pts.length < (closed ? 3 : 2)) return;
    const cmds = [{ op: 'M', xy: pts[0] }].concat(pts.slice(1).map(q => ({ op: 'D', xy: q })));
    if (closed) cmds.push({ op: 'D', xy: pts[0] });
    setTool('select');
    addContours([{ cmds }], closed ? 'Контур замкнут и выбран.' : 'Линия готова и выбрана. Нож прорежет её как есть, без замыкания.');
  };
  // Рамка фигуры под мышью: Ctrl — квадрат или круг, Shift — от центра
  const drawFrame = d => {
    let w = d.end.x - d.start.x;
    let h = d.end.y - d.start.y;
    if (d.square) {
      const s = Math.max(Math.abs(w), Math.abs(h));
      w = (w < 0 ? -1 : 1) * s;
      h = (h < 0 ? -1 : 1) * s;
    }
    const x0 = d.fromCenter ? d.start.x - Math.abs(w) : Math.min(d.start.x, d.start.x + w);
    const y0 = d.fromCenter ? d.start.y - Math.abs(h) : Math.min(d.start.y, d.start.y + h);
    const fw = d.fromCenter ? 2 * Math.abs(w) : Math.abs(w);
    const fh = d.fromCenter ? 2 * Math.abs(h) : Math.abs(h);
    // Углы рамки — в целых единицах: размер ровно тот, что под мышью
    const minX = Math.round(x0);
    const minY = Math.round(y0);
    const maxX = Math.round(x0 + fw);
    const maxY = Math.round(y0 + fh);
    return { type: d.tool, cx: (minX + maxX) / 2, cy: (minY + maxY) / 2, w: maxX - minX, h: maxY - minY, r: 0, angle: 0 };
  };

  // --- Мышь ---
  const onWheel = e => {
    e.preventDefault();
    const p = toUnits(e);
    zoomAt(e.deltaY < 0 ? 1.15 : 1 / 1.15, p.sx, p.sy);
  };
  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return undefined;
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  });

  const startDrag = d => {
    dragRef.current = d;
    setDrag(d);
  };
  // С верхней линейки тянется горизонтальная направляющая, с левой — вертикальная
  const onRulerDown = axis => e => {
    if (e.button !== 0) return;
    e.preventDefault();
    const p = toUnits(e);
    startDrag({ kind: 'guide', id: null, axis, v: axis === 'x' ? p.x : p.y, inside: false, marks: [] });
  };

  const onMouseDown = e => {
    const p = toUnits(e);
    const s = stateRef.current;
    if (e.button === 1 || (e.button === 0 && space)) {
      e.preventDefault();
      startDrag({ kind: 'pan', start: p, view: viewRef.current });
      return;
    }
    if (e.button !== 0) return;
    const { guide } = e.target.dataset;
    if (guide && (tool === 'select' || tool === 'node')) {
      const g = guides.find(x => String(x.id) === guide);
      if (g) {
        startDrag({ kind: 'guide', id: g.id, axis: g.axis, v: g.v, inside: true, marks: [] });
        return;
      }
    }
    if (tool === 'rect' || tool === 'ellipse') {
      const sp = snapPoint([p.x, p.y]);
      const start = { ...p, x: sp.q[0], y: sp.q[1] };
      startDrag({ kind: 'draw', tool, start, end: start, marks: sp.marks });
      return;
    }
    if (tool === 'scissors') {
      if (e.shiftKey) {
        addCutPoint(p);
        return;
      }
      const h = pieceAt(p);
      if (h) deletePiece(h);
      else say('Наведите на контур: кусок, который удалится, подсветится красным.');
      return;
    }
    if (tool === 'node') {
      const { node, hpart, hwhich, item } = e.target.dataset;
      const key = nodeSelRef.current.key;
      if (node !== undefined && keyExists(key)) {
        const k = +node;
        let nodes = nodeSelRef.current.nodes;
        if (e.shiftKey) nodes = nodes.includes(k) ? nodes.filter(x => x !== k) : nodes.concat([k]);
        else if (!nodes.includes(k)) nodes = [k];
        setNodeSel({ key, nodes });
        setCornerDraft(null);
        if (nodes.includes(k)) {
          startDrag({ kind: 'nodes', key, base: pathOf(key), nodes, grab: pathNodes(pathOf(key))[k], start: p, marks: [] });
        }
        return;
      }
      if (hpart !== undefined && keyExists(key)) {
        startDrag({ kind: 'handle', key, base: pathOf(key), part: +hpart, which: hwhich, start: p, marks: [] });
        return;
      }
      if (item) {
        setNodeSel({ key: item, nodes: [] });
        setCornerDraft(null);
        if (!s.selected[item] || Object.keys(s.selected).length !== 1) dispatch({ type: 'select', keys: { [item]: true } });
        return;
      }
      startDrag({ kind: 'nodeband', start: p, end: p, add: e.shiftKey });
      return;
    }
    if (tool === 'start') {
      const t = startTarget(p);
      if (t) setStart(t);
      else say('Щёлкните ближе к точке контура: точки видны кружками.');
      return;
    }
    if (tool === 'poly') {
      const pts = polyRef.current || [];
      if (pts.length >= 3) {
        const v = viewRef.current;
        const [fx, fy] = pts[0];
        if (Math.hypot(v.ox + fx * v.k - p.sx, v.oy - fy * v.k - p.sy) <= 8) {
          finishPoly(true);
          return;
        }
      }
      if (e.detail > 1) return; // второй щелчок двойного: линию закончит dblclick
      const q = polyPoint(p, e.shiftKey);
      const last = pts[pts.length - 1];
      if (!last || last[0] !== q[0] || last[1] !== q[1]) setPoly(pts.concat([q]));
      return;
    }
    const { handle } = e.target.dataset;
    const { item } = e.target.dataset;
    if (handle && selBox) {
      startDrag({ kind: 'scale', handle, start: p, box: selBox, m: null });
      return;
    }
    if (item) {
      if (e.shiftKey) {
        dispatch({ type: 'toggle', key: item });
        return;
      }
      if (!s.selected[item]) dispatch({ type: 'select', keys: { [item]: true } });
      const moving = s.selected[item] ? s.selected : { [item]: true };
      const box = unionOf(itemsFor(s).filter(it => moving[it.key]));
      startDrag({ kind: 'move', start: p, m: null, moving, box, grab: grabNode([p.x, p.y], moving) });
      return;
    }
    if (selBox && p.x >= selBox.minX && p.x <= selBox.maxX && p.y >= selBox.minY && p.y <= selBox.maxY) {
      startDrag({ kind: 'move', start: p, m: null, moving: s.selected, box: selBox, grab: grabNode([p.x, p.y], s.selected) });
      return;
    }
    startDrag({ kind: 'band', start: p, end: p, add: e.shiftKey });
  };

  const onDoubleClick = e => {
    if (tool === 'poly') {
      if (polyRef.current) finishPoly(false);
      return;
    }
    const { guide } = e.target.dataset;
    if (guide) {
      setGuideDialog({ id: guide });
      return;
    }
    if (tool === 'node') {
      if (e.target.dataset.node === undefined) {
        const p = toUnits(e);
        insertNodeAt([p.x, p.y]);
      }
      return;
    }
    const { item } = e.target.dataset;
    if (tool !== 'select' || !item || item[0] !== 'x') return;
    const k = +item.slice(1);
    const x = stateRef.current.extra[k];
    if (x && x.shape) openShapeDialog({ k, shape: x.shape });
  };

  useEffect(() => {
    const onMove = e => {
      if (!canvasRef.current) return;
      const d = dragRef.current;
      const p = toUnits(e);
      altRef.current = e.altKey;
      setCursor(d || canvasRef.current.contains(e.target) ? { ...p, shift: e.shiftKey } : null);
      if (!d) {
        if (tool === 'scissors' && canvasRef.current.contains(e.target)) {
          const h = pieceAt(p);
          setHoverPiece(prev => {
            if (!h && !prev) return prev;
            if (h && prev && h.key === prev.key && h.idx === prev.idx && h.pieces.length === prev.pieces.length) return prev;
            return h;
          });
        }
        return;
      }
      if (d.kind === 'pan') {
        setView({ ...d.view, ox: d.view.ox + (p.sx - d.start.sx), oy: d.view.oy + (p.sy - d.start.sy) });
        return;
      }
      let next = null;
      if (d.kind === 'band' || d.kind === 'nodeband') next = { ...d, end: p };
      if (d.kind === 'draw') {
        const sp = snapPoint([p.x, p.y]);
        next = { ...d, end: { ...p, x: sp.q[0], y: sp.q[1] }, marks: sp.marks, square: e.ctrlKey || e.metaKey, fromCenter: e.shiftKey };
      }
      if (d.kind === 'move') {
        let dx = p.x - d.start.x;
        let dy = p.y - d.start.y;
        // Ctrl — только по одной оси
        if (e.ctrlKey) { if (Math.abs(dx) > Math.abs(dy)) dy = 0; else dx = 0; }
        const sn = d.box ? snapMove(d, dx, dy) : { dx, dy, marks: [] };
        if (e.ctrlKey) { if (Math.abs(dx) > Math.abs(dy)) sn.dy = 0; else sn.dx = 0; }
        next = { ...d, m: translate(sn.dx, sn.dy), dx: sn.dx, dy: sn.dy, marks: sn.marks };
      }
      if (d.kind === 'nodes') {
        let dx = p.x - d.start.x;
        let dy = p.y - d.start.y;
        if (e.ctrlKey) { if (Math.abs(dx) > Math.abs(dy)) dy = 0; else dx = 0; }
        const sp = snapPoint([d.grab[0] + dx, d.grab[1] + dy], t => t.key === d.key && d.nodes.includes(t.k));
        const delta = [sp.q[0] - d.grab[0], sp.q[1] - d.grab[1]];
        if (e.ctrlKey) { if (Math.abs(dx) > Math.abs(dy)) delta[1] = 0; else delta[0] = 0; }
        const path = moveNodes(d.base, d.nodes, delta);
        next = { ...d, path, moved: Math.abs(dx) + Math.abs(dy) > 0.5, marks: sp.marks, overrides: { [d.key]: pathToCmds(path) } };
      }
      if (d.kind === 'handle') {
        const sp = snapPoint([p.x, p.y]);
        const path = moveHandle(d.base, d.part, d.which, sp.q);
        next = { ...d, path, moved: true, marks: sp.marks, overrides: { [d.key]: pathToCmds(path) } };
      }
      if (d.kind === 'guide') {
        const r = canvasRef.current.getBoundingClientRect();
        const inside = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
        const sg = snapGuide(d.axis, d.axis === 'x' ? p.x : p.y);
        next = { ...d, v: sg.v, inside, marks: sg.marks };
      }
      if (d.kind === 'scale') {
        const b = d.box;
        const h = d.handle;
        const center = e.shiftKey;
        // край рамки липнет к направляющим и краю листа
        const sa = snapping() ? snapAxes([p.x, p.y]) : { q: [p.x, p.y], marks: [] };
        const px = sa.q[0];
        const py = sa.q[1];
        const w0 = b.maxX - b.minX || 1;
        const h0 = b.maxY - b.minY || 1;
        let ax = h.includes('w') ? b.maxX : b.minX;
        let ay = h.includes('s') ? b.maxY : b.minY;
        if (center) { ax = (b.minX + b.maxX) / 2; ay = (b.minY + b.maxY) / 2; }
        const k = center ? 2 : 1;
        let sx = h.includes('e') || h.includes('w') ? (k * Math.abs(px - ax)) / w0 : 1;
        let sy = h.includes('n') || h.includes('s') ? (k * Math.abs(py - ay)) / h0 : 1;
        if (h.length === 2) { const s = Math.max(sx, sy); sx = s; sy = s; }
        sx = Math.max(sx, 0.01);
        sy = Math.max(sy, 0.01);
        next = { ...d, m: scaleAbout(sx, sy, ax, ay), sx, sy, marks: sa.marks };
      }
      if (next) {
        dragRef.current = next;
        setDrag(next);
      }
    };
    const onUp = () => {
      const d = dragRef.current;
      if (!d) return;
      dragRef.current = null;
      setDrag(null);
      if ((d.kind === 'move' || d.kind === 'scale') && d.m) {
        const moved = d.kind === 'scale' || Math.abs(d.dx) >= 0.5 || Math.abs(d.dy) >= 0.5;
        if (moved) commitTransform(d.m);
      } else if (d.kind === 'draw') {
        const shape = drawFrame(d);
        if (shape.w >= UNITS_PER_MM && shape.h >= UNITS_PER_MM) {
          setTool('select'); // нарисовал — сразу можно двигать и менять размер
          addContours([{ cmds: shapeCmds(shape), shape }], shape.type === 'rect'
            ? 'Прямоугольник готов. Точный размер и радиус углов: в полях сверху или двойным щелчком.'
            : 'Эллипс готов. Точный размер: в полях сверху или двойным щелчком.');
        }
      } else if (d.kind === 'nodes' || d.kind === 'handle') {
        if (d.moved && d.path) commitEdit({ set: { [d.key]: pathToCmds(d.path) }, select: [d.key] });
      } else if (d.kind === 'nodeband') {
        const key = nodeSelRef.current.key;
        if (!keyExists(key)) return;
        const x0 = Math.min(d.start.x, d.end.x);
        const x1 = Math.max(d.start.x, d.end.x);
        const y0 = Math.min(d.start.y, d.end.y);
        const y1 = Math.max(d.start.y, d.end.y);
        const tiny = Math.abs(d.end.sx - d.start.sx) < 3 && Math.abs(d.end.sy - d.start.sy) < 3;
        const inBand = [];
        if (!tiny) pathNodes(pathOf(key)).forEach((q, k) => { if (q[0] >= x0 && q[0] <= x1 && q[1] >= y0 && q[1] <= y1) inBand.push(k); });
        const prev = d.add ? nodeSelRef.current.nodes : [];
        setNodeSel({ key, nodes: Array.from(new Set(prev.concat(inBand))) });
        setCornerDraft(null);
      } else if (d.kind === 'guide') {
        if (d.inside) {
          if (d.id === null) saveGuides(guides.concat([{ id: `g${Date.now()}${Math.floor(Math.random() * 1000)}`, axis: d.axis, v: d.v }]));
          else saveGuides(guides.map(g => (g.id === d.id ? { ...g, v: d.v } : g)));
        } else if (d.id !== null) {
          saveGuides(guides.filter(g => g.id !== d.id));
          say('Направляющая убрана.');
        }
      } else if (d.kind === 'band') {
        const x0 = Math.min(d.start.x, d.end.x);
        const x1 = Math.max(d.start.x, d.end.x);
        const y0 = Math.min(d.start.y, d.end.y);
        const y1 = Math.max(d.start.y, d.end.y);
        const tiny = Math.abs(d.end.sx - d.start.sx) < 3 && Math.abs(d.end.sy - d.start.sy) < 3;
        const keys = {};
        if (!tiny) {
          itemsFor(stateRef.current).forEach(it => {
            if (it.box.minX >= x0 && it.box.maxX <= x1 && it.box.minY >= y0 && it.box.maxY <= y1) keys[it.key] = true;
          });
        }
        dispatch({ type: 'select', keys, add: d.add });
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  });

  // --- Клавиатура ---
  useEffect(() => {
    const onKey = e => {
      altRef.current = e.altKey;
      if (saveOpen || shapeOpen || guideDialog) return;
      const tag = (e.target && e.target.tagName) || '';
      if (/INPUT|TEXTAREA|SELECT/.test(tag)) return;
      const ctrl = e.ctrlKey || e.metaKey;
      const key = e.key;
      // Сочетания по физической клавише: работают и в русской раскладке
      const code = e.code;
      if (key === ' ') { setSpace(true); e.preventDefault(); return; }
      if (polyRef.current) {
        if (key === 'Enter') { finishPoly(false); e.preventDefault(); return; }
        if (key === 'Backspace') {
          const rest = polyRef.current.slice(0, -1);
          setPoly(rest.length ? rest : null);
          e.preventDefault();
          return;
        }
        if (key === 'Escape') { setPoly(null); return; }
      }
      if (key === 'F10') { setTool('node'); e.preventDefault(); return; }
      if (key === 'Alt') e.preventDefault(); // не уводить фокус в меню браузера
      if (tool === 'node' && !nodeSelRef.current.nodes.length && (key === 'Delete' || key === 'Backspace')) {
        say('Выберите узлы, которые удалить. Весь контур удаляется инструментом выбора.');
        e.preventDefault();
        return;
      }
      if (tool === 'node' && nodeSelRef.current.nodes.length) {
        if (key === 'Delete' || key === 'Backspace') { deleteSelectedNodes(); e.preventDefault(); return; }
        if (key === 'Escape') { setNodeSel({ key: nodeSelRef.current.key, nodes: [] }); return; }
        const st = e.shiftKey ? UNITS_PER_MM : 1;
        const arrows = { ArrowLeft: [-st, 0], ArrowRight: [st, 0], ArrowUp: [0, st], ArrowDown: [0, -st] };
        if (arrows[key]) { moveSelectedNodes(arrows[key]); e.preventDefault(); return; }
      }
      if (key === 'F5') { setTool('poly'); e.preventDefault(); return; }
      if (key === 'F6') { setTool('rect'); e.preventDefault(); return; }
      if (key === 'F7') { setTool('ellipse'); e.preventDefault(); return; }
      if (ctrl && code === 'KeyZ' && !e.shiftKey) { dispatch({ type: 'undo' }); e.preventDefault(); return; }
      if (ctrl && ((code === 'KeyZ' && e.shiftKey) || code === 'KeyY')) { dispatch({ type: 'redo' }); e.preventDefault(); return; }
      if (ctrl && code === 'KeyA') { selectAll(); e.preventDefault(); return; }
      if (ctrl && code === 'KeyD') { duplicateSelected(); e.preventDefault(); return; }
      if (ctrl && code === 'KeyC') { copySelected(false); e.preventDefault(); return; }
      if (ctrl && code === 'KeyX') { copySelected(true); e.preventDefault(); return; }
      if (ctrl && code === 'KeyV') { paste(); e.preventDefault(); return; }
      if (key === 'Delete' || key === 'Backspace') { removeSelected(); e.preventDefault(); return; }
      if (key === 'Escape') {
        if (dragRef.current) { dragRef.current = null; setDrag(null); } else if (tool !== 'select') setTool('select');
        else dispatch({ type: 'select', keys: {} });
        return;
      }
      if (key === 'F4') { fitAll(); e.preventDefault(); return; }
      if (key === '+' || key === '=') { zoomAt(1.25, size.w / 2, size.h / 2); return; }
      if (key === '-') { zoomAt(1 / 1.25, size.w / 2, size.h / 2); return; }
      const step = e.shiftKey ? UNITS_PER_MM : 1;
      const moves = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, step], ArrowDown: [0, -step] };
      if (moves[key] && Object.keys(stateRef.current.selected).length) {
        moveBy(moves[key][0], moves[key][1]);
        e.preventDefault();
      }
    };
    const onKeyUp = e => {
      altRef.current = e.altKey;
      if (e.key === ' ') setSpace(false);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('keyup', onKeyUp);
    };
  });

  // Смена инструмента обрывает недорисованную ломаную
  useEffect(() => {
    if (tool !== 'poly') setPoly(null);
    setHoverPiece(null);
    if (tool === 'node') {
      // узлы того контура, что выбран
      const keys = Object.keys(stateRef.current.selected);
      if (!keyExists(nodeSelRef.current.key) && keys.length === 1) setNodeSel({ key: keys[0], nodes: [] });
    } else if (tool !== 'scissors') {
      setCornerDraft(null);
    }
  }, [tool]);

  // Уйти со страницы с несохранёнными правками — браузер спросит
  useEffect(() => {
    if (!dirty) return undefined;
    const onBefore = e => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onBefore);
    return () => window.removeEventListener('beforeunload', onBefore);
  }, [dirty]);

  // --- Проверки перед сохранением ---
  const problems = [];
  const notes = [];
  if (!items.length && !isCreate) problems.push('Удалены все контуры: сохранять нечего.');
  if (!isCreate && boxOk(savedBox) && (savedBox.minX > SHIFT_THRESHOLD || savedBox.minY > SHIFT_THRESHOLD)) {
    problems.push('Лекало целиком дальше 300 мм от края листа: плоттер повернёт его. Подвиньте ближе к нулю.');
  }
  if (isCreate && (shift[0] || shift[1])) {
    notes.push(`При сохранении лекало встанет в угол листа, как остальные лекала: X ${formatMm(corner.x)}, Y ${formatMm(corner.y)} мм.`);
  } else if (shift[0] || shift[1]) {
    notes.push(`Край лекала ушёл за начало листа. При сохранении лекало целиком встанет в угол, как исходное: сдвиг X ${formatMm(shift[0])}, Y ${formatMm(shift[1])} мм.`);
  }
  const canSave = items.length > 0 && problems.length === 0;
  if (sheet && boxOk(savedBox) && (savedBox.maxX + shift[0] > sheet.w || savedBox.maxY + shift[1] > sheet.h)) {
    notes.push('Лекало выходит за лист.');
  }

  // --- Сохранение ---
  const buildPrn = () => {
    const s = stateRef.current;
    const [dx, dy] = saveShift(unionOf(itemsFor(s)));
    if (!dx && !dy) return serializePrn(doc, refs, s.xy, s.deleted, s.extra, s.replaced);
    const m = translate(dx, dy);
    const all = {};
    doc.contours.forEach((_, i) => { all[i] = true; });
    const replaced = {};
    Object.keys(s.replaced).forEach(i => { replaced[i] = transformCmds(s.replaced[i], m); });
    const extra = s.extra.map(x => ({ cmds: transformCmds(x.cmds, m) }));
    return serializePrn(doc, refs, applyTransform(s.xy, refs, all, m), s.deleted, extra, replaced);
  };
  const latin1 = text => {
    const out = new Uint8Array(text.length);
    for (let i = 0; i < text.length; i += 1) out[i] = text.charCodeAt(i) & 0xff;
    return out;
  };
  const download = name => {
    const blob = new Blob([latin1(buildPrn())], { type: 'application/octet-stream' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${name}.prn`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 1000);
  };
  const makeFiles = async name => {
    const prn = new File([latin1(buildPrn())], `${name}.prn`, { type: 'application/octet-stream' });
    const current = itemsFor(stateRef.current);
    const box = unionOf(current);
    const fit = variant.preview && variant.preview.fit;
    const portraitPreview = previewPortrait !== undefined ? previewPortrait : Boolean(fit && fit.height > fit.width);
    const landscapeGeometry = box.maxX - box.minX > box.maxY - box.minY;
    const blob = await renderPreviewPng(current.map(it => it.segs), box, { rotate: Boolean(portraitPreview && landscapeGeometry) });
    const preview = new File([blob], `${name}.png`, { type: 'image/png' });
    return { prn, preview };
  };

  // --- Отрисовка ---
  const toScreen = (x, y) => [view.ox + x * view.k, view.oy - y * view.k];
  const svgTransform = `matrix(${view.k} 0 0 ${-view.k} ${view.ox} ${view.oy})`;
  const allPath = useMemo(() => fillPaths(items.map(it => it.segs)).map(segmentsToSvg).join(' '), [items]);

  const { major, minor } = rulerStep(view.k);
  const ticks = (from, to, axis) => {
    const out = [];
    const minorU = minor * UNITS_PER_MM;
    const start = Math.floor(from / minorU) * minorU;
    for (let u = start; u <= to; u += minorU) {
      const mmVal = Math.round(u / UNITS_PER_MM * 1000) / 1000;
      const isMajor = Math.abs(mmVal / major - Math.round(mmVal / major)) < 1e-6;
      out.push({ u, mm: mmVal, isMajor, axis });
    }
    return out;
  };
  const xFrom = (0 - view.ox) / view.k;
  const xTo = (size.w - view.ox) / view.k;
  const yFrom = (view.oy - size.h) / view.k;
  const yTo = view.oy / view.k;

  let selRect = null;
  let handles = [];
  let dimLabel = null;
  if (selBox && boxOk(selBox)) {
    const [x0, y0] = toScreen(selBox.minX, selBox.maxY);
    const [x1, y1] = toScreen(selBox.maxX, selBox.minY);
    selRect = { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
    const mx = (x0 + x1) / 2;
    const my = (y0 + y1) / 2;
    if (tool === 'select') {
      handles = [
        ['nw', x0, y0], ['n', mx, y0], ['ne', x1, y0], ['e', x1, my],
        ['se', x1, y1], ['s', mx, y1], ['sw', x0, y1], ['w', x0, my],
      ];
    }
    const w = mmText(selBox.maxX - selBox.minX);
    const h = mmText(selBox.maxY - selBox.minY);
    let extraText = '';
    if (drag && drag.kind === 'scale' && drag.sx) {
      const same = Math.abs(drag.sx - drag.sy) < 1e-9;
      extraText = same
        ? ` (${Math.round(drag.sx * 1000) / 10}%)`
        : ` (${Math.round(drag.sx * 1000) / 10}% × ${Math.round(drag.sy * 1000) / 10}%)`;
    }
    if (drag && drag.kind === 'move' && drag.m) {
      extraText = `, сдвиг X ${formatMm(drag.dx)}, Y ${formatMm(drag.dy)}`;
    }
    dimLabel = { x: mx, y: y1 + 18, text: `${w} × ${h} мм${extraText}` };
  }
  let band = null;
  if (drag && drag.kind === 'band') {
    band = {
      x: Math.min(drag.start.sx, drag.end.sx), y: Math.min(drag.start.sy, drag.end.sy),
      w: Math.abs(drag.end.sx - drag.start.sx), h: Math.abs(drag.end.sy - drag.start.sy),
    };
  }
  // Что рисуется сейчас: фигура под мышью или ломаная с линией до курсора
  let drawing = null;
  if (drag && drag.kind === 'draw') {
    const shape = drawFrame(drag);
    const [lx, ly] = toScreen(shape.cx, shape.cy - shape.h / 2);
    drawing = {
      d: segmentsToSvg(cmdsSegments(shapeCmds(shape))),
      label: { x: lx, y: ly + 18, text: `${mmText(shape.w)} × ${mmText(shape.h)} мм` },
    };
  } else if (tool === 'poly' && poly) {
    const pts = cursor ? poly.concat([polyPoint(cursor, cursor.shift)]) : poly;
    let label = null;
    if (cursor) {
      const a = poly[poly.length - 1];
      const b = pts[pts.length - 1];
      const [lx, ly] = toScreen(b[0], b[1]);
      label = { x: lx + 14, y: ly - 12, text: `${mmText(Math.hypot(b[0] - a[0], b[1] - a[1]))} мм` };
    }
    drawing = {
      d: pts.map((q, i) => `${i ? 'L' : 'M'}${q[0]} ${q[1]}`).join(' '),
      label,
      first: poly.length >= 3 ? toScreen(poly[0][0], poly[0][1]) : null,
    };
  }

  // Направление реза: точка начала, стрелки по ходу ножа, номер по порядку
  const flat = useMemo(() => items.map(it => flattenSegments(it.segs)), [items]);
  const dirOn = showDir || tool === 'start';
  const dirMarks = useMemo(() => {
    if (!dirOn) return [];
    const { k, ox, oy } = view;
    const firstAt = 24 / k; // первая стрелка рядом с началом, дальше через 110 px
    const step = 110 / k;
    return items.map((it, n) => {
      const pts = flat[n];
      const arrows = [];
      let at = firstAt;
      let passed = 0;
      for (let i = 2; i + 1 < pts.length && arrows.length < 40; i += 2) {
        const x0 = pts[i - 2];
        const y0 = pts[i - 1];
        const dx = pts[i] - x0;
        const dy = pts[i + 1] - y0;
        const len = Math.hypot(dx, dy);
        while (len > 0 && at <= passed + len && arrows.length < 40) {
          const t = (at - passed) / len;
          arrows.push({ x: ox + (x0 + dx * t) * k, y: oy - (y0 + dy * t) * k, a: (Math.atan2(-dy, dx) * 180) / Math.PI });
          at += step;
        }
        passed += len;
      }
      return { key: it.key, n: n + 1, x: ox + pts[0] * k, y: oy - pts[1] * k, arrows };
    });
  }, [dirOn, items, flat, view]);
  // Узлы, с которых можно начать рез (для инструмента «Начало реза»)
  const vertexMarks = tool === 'start'
    ? items.reduce((out, it) => out.concat(cmdsVertices(itemCmds(state, it.key)).points.map(q => toScreen(q[0], q[1]))), [])
    : [];

  // Узлы и ручки контура, что правится
  const nodeMarks = [];
  const handleMarks = [];
  if (activePath && !(cornerDraft && cornerDraft.preview)) {
    const shown = drag && drag.path && drag.key === activeKey ? drag.path : activePath;
    const n = shown.parts.length;
    pathNodes(shown).forEach((q, k) => {
      const [x, y] = toScreen(q[0], q[1]);
      const sel = nodeSel.nodes.includes(k);
      nodeMarks.push({ k, x, y, sel, first: k === 0 });
      if (!sel) return;
      const outPart = k < n ? shown.parts[k] : null;
      const inIdx = k > 0 ? k - 1 : (shown.closed ? n - 1 : null);
      const inPart = inIdx !== null ? shown.parts[inIdx] : null;
      if (outPart && outPart.type === 'C') {
        const [hx, hy] = toScreen(outPart.c1[0], outPart.c1[1]);
        handleMarks.push({ part: k, which: 'c1', x, y, hx, hy });
      }
      if (inPart && inPart.type === 'C') {
        const [hx, hy] = toScreen(inPart.c2[0], inPart.c2[1]);
        handleMarks.push({ part: inIdx, which: 'c2', x, y, hx, hy });
      }
    });
  }
  const oneNode = activePath && nodeSel.nodes.length === 1 ? pathNodes(activePath)[nodeSel.nodes[0]] : null;
  const cornerSel = tool === 'node' ? cornerNodes() : [];
  const draft = cornerDraft && cornerDraft.key === activeKey ? cornerDraft : null;
  const sliderOn = Boolean(draft || cornerSel.length);
  let sliderMax = 0;
  if (draft) sliderMax = draft.maxR;
  else if (cornerSel.length) sliderMax = Math.min(...cornerSel.map(k => cornerAt(activePath, k).maxR));
  const sliderMaxMm = Math.max(0.1, Math.floor(sliderMax) / UNITS_PER_MM);
  const nodePartTypes = activePath ? nodeSel.nodes.map(k => activePath.parts[partOfNode(activePath, k)]).filter(Boolean).map(pt => pt.type) : [];

  // Ножницы: кусок под мышью и точки разреза этого контура
  const hover = tool === 'scissors' && hoverPiece && keyExists(hoverPiece.key) ? hoverPiece : null;
  const cutMarks = hover ? hover.pieces.map(pc => toScreen(pc.parts[0].p0[0], pc.parts[0].p0[1])) : [];

  // Направляющие: та, что тянут, — на новом месте
  const guideDrag = drag && drag.kind === 'guide' ? drag : null;
  const shownGuides = guides
    .filter(g => !(guideDrag && guideDrag.id === g.id))
    .concat(guideDrag ? [{ id: guideDrag.id || 'new', axis: guideDrag.axis, v: guideDrag.v, dragging: true, away: !guideDrag.inside }] : []);
  const snapMarks = (drag && drag.marks) || [];

  const selCount = selItems.length;
  const hasSel = selCount > 0;
  const [axPos, ayPos] = selBox ? anchorOf(selBox) : [null, null];
  const zoomPercent = Math.round((view.k * UNITS_PER_MM) / 3.7795 * 100);
  const device = variant.device;
  let canvasMode = '';
  if (space || (drag && drag.kind === 'pan')) canvasMode = 'is-pan';
  else if (tool === 'node') canvasMode = 'is-node';
  else if (tool === 'scissors') canvasMode = hover ? 'is-cut' : 'is-draw';
  else if (tool !== 'select') canvasMode = 'is-draw';
  let hint = TOOL_HINTS[tool];
  if (hasSel && tool === 'select') hint = `Выбрано контуров: ${selCount}, ${mmText(selBox.maxX - selBox.minX)} × ${mmText(selBox.maxY - selBox.minY)} мм`;
  if (tool === 'node' && activeKey && nodeSel.nodes.length) hint = `Выбрано узлов: ${nodeSel.nodes.length}. Тяните их мышью или стрелками, Delete: удалить.`;

  return (
    <div className="prn-editor">
      <div className="prn-editor__top">
        <div className="prn-editor__title">
          <Link to="/library/brands/">Бренды</Link>
          <span>/</span>
          <Link to={`/library/devices/${device.brand.id}/`}>{device.brand.name}</Link>
          <span>/</span>
          <Link to={`/library/variants/${device.id}/`}>{device.name}</Link>
          <span>/</span>
          <strong>{isCreate ? NEW_NAME : `Редактор лекала «${variant.name}»`}</strong>
          {dirty && <em className="prn-editor__dirty">есть изменения</em>}
        </div>
        <div className="prn-editor__actions">
          <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => download(isCreate ? NEW_NAME : `${variant.name} (изм.)`)} disabled={!items.length}>
            скачать .prn
          </button>
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setSaveOpen(true)} disabled={!canSave}>
            {isCreate ? 'Сохранить лекало' : 'Сохранить как новое лекало'}
          </button>
          <Link
            to={`/library/variants/${device.id}/`}
            className="btn btn-secondary btn-sm"
            onClick={e => {
              if (dirty && !window.confirm('Закрыть редактор? Несохранённые правки пропадут.')) e.preventDefault();
            }}
          >
            Закрыть
          </Link>
        </div>
      </div>

      {shifted && (
        <div className="prn-editor__note">
          Лекало лежало на повёрнутом листе. Здесь оно показано и сохранится так, как его режет плоттер.
        </div>
      )}

      <div className="prn-editor__bar">
        {tool === 'node' ? (
          <div className="prn-editor__row">
            <span className="prn-editor__label">{activeKey ? `Узлы контура: выбрано ${nodeSel.nodes.length}` : 'Щёлкните по контуру'}</span>
            <NumField label="Узел X" title="Положение выбранного узла" value={oneNode ? mmValue(oneNode[0]) : null} disabled={!oneNode} suffix="мм" onCommit={v => setNodePos(0, v)} />
            <NumField label="Y" title="Положение выбранного узла" value={oneNode ? mmValue(oneNode[1]) : null} disabled={!oneNode} suffix="мм" onCommit={v => setNodePos(1, v)} />
            <span className="prn-editor__sep" />
            <label className="prn-editor__field prn-editor__slider" title="Скругление выбранных углов: тяните ползунок и отпустите. Можно двигать ещё раз">
              <span>Скругление угла</span>
              <input
                type="range"
                min="0"
                max={sliderMaxMm}
                step="0.1"
                value={draft ? draft.r / UNITS_PER_MM : 0}
                disabled={!sliderOn}
                onChange={e => previewCorner(parseFloat(e.target.value))}
                onMouseUp={commitCorner}
                onTouchEnd={commitCorner}
                onKeyUp={commitCorner}
              />
            </label>
            <NumField
              label=""
              title="Радиус скругления выбранных углов"
              value={sliderOn ? mmValue(draft ? draft.r : 0) : null}
              disabled={!sliderOn}
              width={52}
              suffix="мм"
              onCommit={v => { if (v >= 0) applyCorner(v); }}
            />
            <span className="prn-editor__sep" />
            <button type="button" className="btn btn-light btn-sm" disabled={!nodePartTypes.includes('L')} title="Сделать отрезок после узла кривой: появятся ручки" onClick={() => nodesToType('C')}>В кривую</button>
            <button type="button" className="btn btn-light btn-sm" disabled={!nodePartTypes.includes('C')} title="Сделать отрезок после узла прямым" onClick={() => nodesToType('L')}>В прямую</button>
            <button type="button" className="btn btn-light btn-sm" disabled={!nodeSel.nodes.length || !activeKey} title="Новый узел посередине отрезка после выбранного (или двойной щелчок по линии)" onClick={addNodesMid}>Добавить узел</button>
            <button type="button" className="btn btn-light btn-sm" disabled={!nodeSel.nodes.length || !activeKey} title="Удалить выбранные узлы (Delete)" onClick={deleteSelectedNodes}>Удалить узлы</button>
          </div>
        ) : (
        <div className="prn-editor__row">
          <NumField label="X" title="Положение точки привязки по горизонтали" value={hasSel ? mmValue(axPos) : null} disabled={!hasSel} suffix="мм" onCommit={v => setAnchorPos(0, v)} />
          <NumField label="Y" title="Положение точки привязки по вертикали" value={hasSel ? mmValue(ayPos) : null} disabled={!hasSel} suffix="мм" onCommit={v => setAnchorPos(1, v)} />
          <span className="prn-editor__sep" />
          <NumField label="Ширина" value={hasSel ? mmValue(selBox.maxX - selBox.minX) : null} disabled={!hasSel} suffix="мм" onCommit={v => resizeTo(0, v)} />
          <button
            type="button"
            className={`prn-editor__lock ${lock ? 'is-on' : ''}`}
            title={lock ? 'Пропорции сохраняются. Нажмите, чтобы менять ширину и высоту отдельно' : 'Ширина и высота меняются отдельно. Нажмите, чтобы сохранять пропорции'}
            onClick={() => {
              const next = !lock;
              setLock(next);
              try { localStorage.setItem(LOCK_KEY, next ? '1' : '0'); } catch (e) { /* без памяти */ }
            }}
          >
            {lock ? '🔒' : '🔓'}
          </button>
          <NumField label="Высота" value={hasSel ? mmValue(selBox.maxY - selBox.minY) : null} disabled={!hasSel} suffix="мм" onCommit={v => resizeTo(1, v)} />
          <span className="prn-editor__sep" />
          <div className="prn-editor__anchor" title="Точка привязки: от неё меняется размер, поворот и зеркало">
            {[1, 0.5, 0].map(ay => [0, 0.5, 1].map(ax => (
              <button
                type="button"
                key={`${ax}-${ay}`}
                className={anchor[0] === ax && anchor[1] === ay ? 'is-on' : ''}
                onClick={() => setAnchor([ax, ay])}
                aria-label="точка привязки"
              />
            )))}
          </div>
          <span className="prn-editor__sep" />
          <label className="prn-editor__field" title="Повернуть на угол, против часовой стрелки">
            <span>Повернуть</span>
            <input
              type="text"
              inputMode="decimal"
              value={angle}
              disabled={!hasSel}
              style={{ width: 52 }}
              onChange={e => setAngle(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') { const v = parseMm(angle); if (v) rotate(v); setAngle(''); }
              }}
            />
            <em>°</em>
          </label>
          <button type="button" className="btn btn-light btn-sm" disabled={!hasSel} title="Повернуть на 90° против часовой" onClick={() => rotate(90)}>⟲ 90°</button>
          <button type="button" className="btn btn-light btn-sm" disabled={!hasSel} title="Повернуть на 90° по часовой" onClick={() => rotate(-90)}>⟳ 90°</button>
          <button type="button" className="btn btn-light btn-sm" disabled={!hasSel} title="Отразить слева направо" onClick={() => mirror(0)}>⇋</button>
          <button type="button" className="btn btn-light btn-sm" disabled={!hasSel} title="Отразить сверху вниз" onClick={() => mirror(1)}>⇵</button>
          <span className="prn-editor__sep" />
          <button type="button" className="btn btn-light btn-sm" disabled={!hasSel} title="Резать выбранные контуры в обратную сторону" onClick={reverseSelected}>Обратить</button>
          <button
            type="button"
            className="btn btn-light btn-sm"
            disabled={!items.length}
            title={`Поставить всё лекало в угол листа: X ${formatMm(cornerTarget.x)}, Y ${formatMm(cornerTarget.y)} мм`}
            onClick={toCorner}
          >
            В угол листа
          </button>
        </div>
        )}
        <div className="prn-editor__row">
          <NumField
            label="Радиус"
            title={singleRect ? 'Радиус углов прямоугольника: меняется сразу' : 'Радиус для кнопки «Закруглить»'}
            value={singleRect ? mmValue(singleRect.shape.r || 0) : mmValue(radius * UNITS_PER_MM)}
            width={56}
            suffix="мм"
            onCommit={v => {
              if (v < 0) return;
              rememberRadius(v);
              if (singleRect) roundCorners(v);
            }}
          />
          <button
            type="button"
            className="btn btn-light btn-sm"
            disabled={!hasSel || Boolean(singleRect)}
            title={singleRect ? 'У прямоугольника радиус углов меняется сразу в поле «Радиус»' : 'Скруглить острые углы между прямыми у выбранных контуров'}
            onClick={() => roundCorners(radius)}
          >
            Закруглить
          </button>
          <span className="prn-editor__sep" />
          <button type="button" className="btn btn-light btn-sm" disabled={!hasSel} title="Поставить выбранное по центру самого большого контура лекала, слева направо" onClick={() => alignCenter(0)}>По центру ⇔</button>
          <button type="button" className="btn btn-light btn-sm" disabled={!hasSel} title="Поставить выбранное по центру самого большого контура лекала, сверху вниз" onClick={() => alignCenter(1)}>По центру ⇕</button>
          <span className="prn-editor__sep" />
          <button type="button" className="btn btn-light btn-sm" disabled={!canBoolean} title="Сделать из двух замкнутых контуров один общий" onClick={() => doBoolean('union')}>Объединить</button>
          <button type="button" className="btn btn-light btn-sm" disabled={!canBoolean} title="Из большего контура вырезать меньший" onClick={() => doBoolean('difference')}>Вычесть</button>
          <button type="button" className="btn btn-light btn-sm" disabled={!canBoolean} title="Оставить только общую часть двух контуров" onClick={() => doBoolean('intersection')}>Пересечь</button>
          <span className="prn-editor__sep" />
          <button type="button" className="btn btn-light btn-sm" onClick={selectAll} title="Выбрать все контуры (Ctrl+A)">Выбрать всё</button>
          <button type="button" className="btn btn-light btn-sm" disabled={!hasSel} onClick={() => copySelected(false)} title="Копировать (Ctrl+C). Вставить можно и в другом лекале">Копировать</button>
          <button type="button" className="btn btn-light btn-sm" disabled={!hasSel} onClick={() => copySelected(true)} title="Вырезать (Ctrl+X)">Вырезать</button>
          <button type="button" className="btn btn-light btn-sm" onClick={paste} title="Вставить на то же место (Ctrl+V)">Вставить</button>
          <button type="button" className="btn btn-light btn-sm" disabled={!hasSel} onClick={duplicateSelected} title="Копия со сдвигом 5 мм (Ctrl+D)">Копия</button>
          <button type="button" className="btn btn-light btn-sm" disabled={!hasSel} onClick={removeSelected} title="Удалить выбранные контуры (Delete)">Удалить</button>
          <button type="button" className="btn btn-light btn-sm" disabled={!state.history.length} onClick={() => dispatch({ type: 'undo' })} title="Отменить (Ctrl+Z)">↶</button>
          <button type="button" className="btn btn-light btn-sm" disabled={!state.future.length} onClick={() => dispatch({ type: 'redo' })} title="Вернуть (Ctrl+Y)">↷</button>
        </div>
      </div>

      <div className="prn-editor__body">
        <div className="prn-editor__tools">
          {TOOLS.map(t => (
            <button
              type="button"
              key={t.id}
              className={tool === t.id ? 'is-on' : ''}
              title={t.title}
              data-tool={t.id}
              onClick={() => setTool(t.id)}
            >
              <svg viewBox="0 0 24 24">{t.icon}</svg>
            </button>
          ))}
          <span className="prn-editor__tools-sep" />
          <button
            type="button"
            data-tool="shape"
            title={singleShape ? 'Точный размер выбранной фигуры' : 'Фигура по размеру: прямоугольник или овал с точной шириной, высотой и радиусом углов'}
            onClick={() => openShapeDialog(singleShape)}
          >
            <svg viewBox="0 0 24 24">
              <rect x="3.5" y="7" width="12" height="10" rx="3" />
              <path d="M19 5 L19 19 M17.5 5 L20.5 5 M17.5 19 L20.5 19" />
            </svg>
          </button>
          <span className="prn-editor__tools-sep" />
          <button
            type="button"
            data-tool="direction"
            className={showDir ? 'is-on' : ''}
            title={showDir ? 'Скрыть направление и начало реза' : 'Показать направление и начало реза: точка старта, стрелки, номер по порядку'}
            onClick={() => rememberDir(!showDir)}
          >
            <svg viewBox="0 0 24 24">
              <path d="M4 18 C 6 8, 14 5, 20 10" />
              <path d="M15.5 9 L20 10 L18.5 5.5" />
              <circle cx="4" cy="18" r="1.8" />
            </svg>
          </button>
        </div>

        <div className="prn-editor__stage">
          <div className="prn-editor__corner">мм</div>
          <svg className="prn-editor__ruler prn-editor__ruler_x" width={size.w} height={RULER} style={{ left: RULER }} onMouseDown={onRulerDown('y')}>
            <title>Потяните вниз: горизонтальная направляющая</title>
            {ticks(xFrom, xTo, 0).map(t => {
              const x = view.ox + t.u * view.k;
              return (
                <g key={`x${t.u}`}>
                  <line x1={x} x2={x} y1={t.isMajor ? 6 : 16} y2={RULER} />
                  {t.isMajor && <text x={x + 3} y={11}>{t.mm}</text>}
                </g>
              );
            })}
            {cursor && <line className="prn-editor__cursor-mark" x1={cursor.sx} x2={cursor.sx} y1={0} y2={RULER} />}
          </svg>
          <svg className="prn-editor__ruler prn-editor__ruler_y" width={RULER} height={size.h} style={{ top: RULER }} onMouseDown={onRulerDown('x')}>
            <title>Потяните вправо: вертикальная направляющая</title>
            {ticks(yFrom, yTo, 1).map(t => {
              const y = view.oy - t.u * view.k;
              return (
                <g key={`y${t.u}`}>
                  <line y1={y} y2={y} x1={t.isMajor ? 6 : 16} x2={RULER} />
                  {t.isMajor && <text transform={`translate(11 ${y - 3}) rotate(-90)`}>{t.mm}</text>}
                </g>
              );
            })}
            {cursor && <line className="prn-editor__cursor-mark" y1={cursor.sy} y2={cursor.sy} x1={0} x2={RULER} />}
          </svg>
          <div className="prn-editor__canvas-wrap" style={{ left: RULER, top: RULER }}>
            <svg
              ref={canvasRef}
              className={`prn-editor__canvas ${canvasMode}`}
              width="100%"
              height="100%"
              onMouseDown={onMouseDown}
              onDoubleClick={onDoubleClick}
              onMouseLeave={() => { if (!dragRef.current) setCursor(null); }}
              onContextMenu={e => e.preventDefault()}
            >
              <g transform={svgTransform}>
                {sheet && <rect className="prn-editor__sheet" x={0} y={0} width={sheet.w} height={sheet.h} vectorEffect="non-scaling-stroke" />}
                <line className="prn-editor__axis" x1={0} y1={0} x2={(sheet ? sheet.w : 1000)} y2={0} vectorEffect="non-scaling-stroke" />
                <line className="prn-editor__axis" x1={0} y1={0} x2={0} y2={(sheet ? sheet.h : 1000)} vectorEffect="non-scaling-stroke" />
                {shownGuides.map(g => (
                  <line
                    key={`guide-${g.id}`}
                    className={`prn-editor__guide ${g.dragging ? 'is-drag' : ''} ${g.away ? 'is-away' : ''} ${snapMarks.some(m => m.type === g.axis && m.v === g.v) ? 'is-snap' : ''}`}
                    x1={g.axis === 'x' ? g.v : -1e6}
                    x2={g.axis === 'x' ? g.v : 1e6}
                    y1={g.axis === 'y' ? g.v : -1e6}
                    y2={g.axis === 'y' ? g.v : 1e6}
                    vectorEffect="non-scaling-stroke"
                  />
                ))}
                {(tool === 'select' || tool === 'node') && guides.map(g => (
                  <line
                    key={`gh-${g.id}`}
                    className={`prn-editor__guide-hit ${g.axis === 'x' ? 'is-x' : 'is-y'}`}
                    data-guide={g.id}
                    x1={g.axis === 'x' ? g.v : -1e6}
                    x2={g.axis === 'x' ? g.v : 1e6}
                    y1={g.axis === 'y' ? g.v : -1e6}
                    y2={g.axis === 'y' ? g.v : 1e6}
                    vectorEffect="non-scaling-stroke"
                  />
                ))}
                <path className="prn-editor__fill" d={allPath} fillRule="evenodd" />
                {items.map(it => (
                  <path
                    key={it.key}
                    className={`prn-editor__contour ${state.selected[it.key] ? 'is-selected' : ''}`}
                    d={segmentsToSvg(it.segs)}
                    vectorEffect="non-scaling-stroke"
                  />
                ))}
                {hover && <path className="prn-editor__cut-piece" d={pathD(hover.pieces[hover.idx])} vectorEffect="non-scaling-stroke" />}
                {(tool === 'select' || tool === 'node') && items.map(it => (
                  <path
                    key={`hit-${it.key}`}
                    className="prn-editor__hit"
                    d={segmentsToSvg(it.segs)}
                    data-item={it.key}
                    vectorEffect="non-scaling-stroke"
                  />
                ))}
                {drawing && <path className="prn-editor__drawing" d={drawing.d} vectorEffect="non-scaling-stroke" />}
              </g>
              {!items.length && !drawing && (
                <g className="prn-editor__empty">
                  <text x={size.w / 2} y={size.h / 2 - 10}>Нарисуйте контур: прямоугольник F6, эллипс F7, ломаная F5 или «Фигура по размеру» слева.</text>
                  <text x={size.w / 2} y={size.h / 2 + 14}>Можно вставить контуры из другого лекала: скопируйте их там и нажмите здесь Ctrl+V.</text>
                </g>
              )}
              {isCreate && (
                <g className="prn-editor__corner-mark">
                  <path d={`M${toScreen(cornerTarget.x, cornerTarget.y)[0]} ${toScreen(cornerTarget.x, cornerTarget.y)[1] - 14} l0 14 l14 0`} />
                  <text x={toScreen(cornerTarget.x, cornerTarget.y)[0]} y={toScreen(cornerTarget.x, cornerTarget.y)[1] + 16}>сюда встанет угол лекала</text>
                </g>
              )}
              {dirMarks.map(m => (
                <g key={`dir-${m.key}`} className="prn-editor__dir">
                  {m.arrows.map((a, j) => (
                    <path key={j} className="prn-editor__dir-arrow" d="M-4 -3.5 L3.5 0 L-4 3.5 Z" transform={`translate(${a.x} ${a.y}) rotate(${a.a})`} />
                  ))}
                  <circle className={`prn-editor__dir-start ${m.n === 1 ? 'is-first' : ''}`} cx={m.x} cy={m.y} r={m.n === 1 ? 4.5 : 3.5} />
                  <text className="prn-editor__dir-num" x={m.x + 6} y={m.y - 6}>{m.n}</text>
                </g>
              ))}
              {vertexMarks.map(([vx, vy]) => <circle key={`v${vx}-${vy}`} className="prn-editor__vertex" cx={vx} cy={vy} r={3} />)}
              {guideDrag && cursor && (
                <text className="prn-editor__dims" x={cursor.sx + 14} y={cursor.sy - 10}>
                  {guideDrag.inside ? `${guideDrag.axis === 'x' ? 'X' : 'Y'} ${formatMm(guideDrag.v)} мм` : (guideDrag.id ? 'отпустите: направляющая уберётся' : 'тяните на лист')}
                </text>
              )}
              {handleMarks.map(h => (
                <g key={`h-${h.part}-${h.which}`}>
                  <line className="prn-editor__node-arm" x1={h.x} y1={h.y} x2={h.hx} y2={h.hy} />
                  <circle className="prn-editor__node-handle" data-hpart={h.part} data-hwhich={h.which} cx={h.hx} cy={h.hy} r={4.5} />
                </g>
              ))}
              {nodeMarks.map(n => (
                <rect
                  key={`n-${n.k}`}
                  className={`prn-editor__node ${n.sel ? 'is-on' : ''} ${n.first ? 'is-first' : ''}`}
                  data-node={n.k}
                  x={n.x - 4}
                  y={n.y - 4}
                  width={8}
                  height={8}
                />
              ))}
              {cutMarks.map(([cx, cy]) => <circle key={`cut-${cx}-${cy}`} className="prn-editor__cut-point" cx={cx} cy={cy} r={3.5} />)}
              {snapMarks.map(m => {
                if (m.type === 'point') {
                  const [mx, my] = toScreen(m.p[0], m.p[1]);
                  return <rect key={`sp-${mx}-${my}`} className="prn-editor__snap-point" x={mx - 6} y={my - 6} width={12} height={12} />;
                }
                return m.type === 'x'
                  ? <line key={`sx-${m.v}`} className="prn-editor__snap-line" x1={toScreen(m.v, 0)[0]} x2={toScreen(m.v, 0)[0]} y1={0} y2={size.h} />
                  : <line key={`sy-${m.v}`} className="prn-editor__snap-line" y1={toScreen(0, m.v)[1]} y2={toScreen(0, m.v)[1]} x1={0} x2={size.w} />;
              })}
              {selRect && <rect className="prn-editor__selbox" x={selRect.x} y={selRect.y} width={selRect.w} height={selRect.h} />}
              {handles.map(([name, hx, hy]) => (
                <rect
                  key={name}
                  className={`prn-editor__handle prn-editor__handle_${name}`}
                  data-handle={name}
                  x={hx - HANDLE / 2}
                  y={hy - HANDLE / 2}
                  width={HANDLE}
                  height={HANDLE}
                />
              ))}
              {dimLabel && <text className="prn-editor__dims" x={dimLabel.x} y={dimLabel.y}>{dimLabel.text}</text>}
              {drawing && drawing.first && <circle className="prn-editor__first" cx={drawing.first[0]} cy={drawing.first[1]} r={5} />}
              {drawing && drawing.label && <text className="prn-editor__dims" x={drawing.label.x} y={drawing.label.y}>{drawing.label.text}</text>}
              {band && <rect className="prn-editor__band" x={band.x} y={band.y} width={band.w} height={band.h} />}
              {drag && drag.kind === 'nodeband' && (
                <rect
                  className="prn-editor__band"
                  x={Math.min(drag.start.sx, drag.end.sx)}
                  y={Math.min(drag.start.sy, drag.end.sy)}
                  width={Math.abs(drag.end.sx - drag.start.sx)}
                  height={Math.abs(drag.end.sy - drag.start.sy)}
                />
              )}
            </svg>
          </div>
        </div>
      </div>

      <div className="prn-editor__status">
        <span className="prn-editor__pos">
          {cursor ? `X ${formatMm(cursor.x)}  Y ${formatMm(cursor.y)} мм` : ''}
        </span>
        <span>{hint}</span>
        {flash && <span className="prn-editor__flash">{flash}</span>}
        <span>{boxOk(allBox) ? `Лекало: ${mmText(allBox.maxX - allBox.minX)} × ${mmText(allBox.maxY - allBox.minY)} мм` : ''}</span>
        {problems.concat(notes).map(t => <span key={t} className={problems.includes(t) ? 'is-error' : 'is-warning'}>{t}</span>)}
        <span className="prn-editor__status-right">
          <button
            type="button"
            className={`prn-editor__toggle ${snapOn ? 'is-on' : ''}`}
            title="Привязка к узлам, направляющим и краю листа. Удерживайте Alt, чтобы на время выключить"
            onClick={() => {
              const next = !snapOn;
              setSnapOn(next);
              try { localStorage.setItem(SNAP_KEY, next ? '1' : '0'); } catch (e) { /* без памяти */ }
            }}
          >
            Привязка
          </button>
          <button type="button" className="btn btn-light btn-sm" onClick={() => zoomAt(1 / 1.25, size.w / 2, size.h / 2)} title="Отдалить (−)">−</button>
          <span className="prn-editor__zoom">{zoomPercent}%</span>
          <button type="button" className="btn btn-light btn-sm" onClick={() => zoomAt(1.25, size.w / 2, size.h / 2)} title="Приблизить (+)">+</button>
          <button type="button" className="btn btn-light btn-sm" onClick={fitAll} title="Показать всё лекало (F4)">Всё лекало</button>
        </span>
      </div>

      {guideDialog && guides.some(g => String(g.id) === String(guideDialog.id)) && (
        <GuideDialog
          guide={guides.find(g => String(g.id) === String(guideDialog.id))}
          onCancel={() => setGuideDialog(null)}
          onApply={v => {
            saveGuides(guides.map(g => (String(g.id) === String(guideDialog.id) ? { ...g, v } : g)));
            setGuideDialog(null);
          }}
          onDelete={() => {
            saveGuides(guides.filter(g => String(g.id) !== String(guideDialog.id)));
            setGuideDialog(null);
          }}
          onDeleteAll={() => {
            saveGuides([]);
            setGuideDialog(null);
          }}
        />
      )}

      {shapeOpen && (
        <ShapeDialog
          initial={shapeOpen.initial}
          editing={shapeOpen.edit !== null}
          onCancel={() => setShapeOpen(null)}
          onApply={applyShape}
        />
      )}

      {saveOpen && (
        <SaveDialog
          variant={variant}
          mode={mode}
          onCancel={() => setSaveOpen(false)}
          onDownload={download}
          onSave={async ({ name, isEnabled }) => {
            const saving = snapshot(stateRef.current);
            const files = await makeFiles(name);
            const created = await onSave({ name, isEnabled, ...files });
            setBase(saving);
            return created;
          }}
        />
      )}
    </div>
  );
}

// Окно направляющей: точное положение в мм, удалить эту или все
function GuideDialog({ guide, onCancel, onApply, onDelete, onDeleteAll }) {
  const [text, setText] = useState(formatMm(guide.v));
  useEffect(() => {
    document.body.classList.add('modal-open');
    return () => document.body.classList.remove('modal-open');
  }, []);
  const v = parseMm(text);
  const submit = e => {
    e.preventDefault();
    if (v !== null) onApply(Math.round(v * UNITS_PER_MM));
  };
  return (
    <>
      <div className="modal fade show d-block prn-editor__guide-dialog" tabIndex="-1" role="dialog" onKeyDown={e => { if (e.key === 'Escape') onCancel(); }}>
        <div className="modal-dialog modal-sm" role="document">
          <form className="modal-content" onSubmit={submit}>
            <div className="modal-header">
              <h5 className="modal-title">{guide.axis === 'x' ? 'Вертикальная направляющая' : 'Горизонтальная направляющая'}</h5>
            </div>
            <div className="modal-body">
              <label htmlFor="prn-guide-v">{guide.axis === 'x' ? 'Положение X' : 'Положение Y'}</label>
              <div className="input-group input-group-sm">
                <input id="prn-guide-v" className="form-control" inputMode="decimal" value={text} autoFocus onChange={e => setText(e.target.value)} />
                <div className="input-group-append"><span className="input-group-text">мм</span></div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-link mr-auto" onClick={onDeleteAll}>Убрать все</button>
              <button type="button" className="btn btn-outline-danger" onClick={onDelete}>Убрать</button>
              <button type="submit" className="btn btn-primary" disabled={v === null}>Применить</button>
              <button type="button" className="btn btn-secondary" onClick={onCancel}>Отмена</button>
            </div>
          </form>
        </div>
      </div>
      <div className="modal-backdrop fade show" />
    </>
  );
}

// Окно «Фигура по размеру»: новая фигура или точный размер выбранной.
// Всё в мм; поле, которое не трогали, не меняется даже на долю единицы
function ShapeDialog({ initial, editing, onCancel, onApply }) {
  const [type, setType] = useState(initial.type);
  const [start] = useState(() => ({
    w: formatMm(initial.w),
    h: formatMm(initial.h),
    r: formatMm(initial.r || 0),
    x: formatMm(initial.cx),
    y: formatMm(initial.cy),
    angle: degText(initial.angle),
  }));
  const [f, setF] = useState(start);

  useEffect(() => {
    document.body.classList.add('modal-open');
    return () => document.body.classList.remove('modal-open');
  }, []);

  const v = {};
  Object.keys(f).forEach(key => { v[key] = parseMm(f[key]); });
  const errors = [];
  if (!(v.w > 0) || !(v.h > 0)) errors.push('Ширина и высота должны быть больше нуля.');
  if (type === 'rect' && !(v.r >= 0)) errors.push('Радиус углов: 0 или больше.');
  if (v.x === null || v.y === null) errors.push('Укажите центр.');
  if (v.angle === null) errors.push('Укажите поворот, можно 0.');
  const units = (key, orig) => (f[key] === start[key] ? orig : Math.round(v[key] * UNITS_PER_MM));

  const submit = e => {
    e.preventDefault();
    if (errors.length) return;
    const w = units('w', initial.w);
    const h = units('h', initial.h);
    onApply({
      type,
      cx: units('x', initial.cx),
      cy: units('y', initial.cy),
      w,
      h,
      r: type === 'rect' ? Math.min(units('r', initial.r || 0), w / 2, h / 2) : 0,
      angle: f.angle === start.angle ? initial.angle || 0 : v.angle,
    });
  };

  const input = (key, label, suffix, autoFocus) => (
    <div className="form-group col-4">
      <label htmlFor={`prn-shape-${key}`}>{label}</label>
      <div className="input-group input-group-sm">
        <input
          id={`prn-shape-${key}`}
          className="form-control"
          inputMode="decimal"
          value={f[key]}
          autoFocus={autoFocus}
          onChange={e => setF({ ...f, [key]: e.target.value })}
        />
        <div className="input-group-append"><span className="input-group-text">{suffix}</span></div>
      </div>
    </div>
  );

  return (
    <>
      <div
        className="modal fade show d-block prn-editor__shape"
        tabIndex="-1"
        role="dialog"
        onKeyDown={e => { if (e.key === 'Escape') onCancel(); }}
      >
        <div className="modal-dialog modal-dialog-scrollable" role="document">
          <form className="modal-content" onSubmit={submit}>
            <div className="modal-header">
              <h5 className="modal-title">{editing ? 'Размер фигуры' : 'Фигура по размеру'}</h5>
            </div>
            <div className="modal-body">
              <div className="btn-group btn-group-sm mb-3" role="group">
                <button type="button" className={`btn ${type === 'rect' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setType('rect')}>Прямоугольник</button>
                <button type="button" className={`btn ${type === 'ellipse' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setType('ellipse')}>Овал или круг</button>
              </div>
              <div className="form-row">
                {input('w', 'Ширина', 'мм', true)}
                {input('h', 'Высота', 'мм')}
                {type === 'rect' && input('r', 'Радиус углов', 'мм')}
              </div>
              <div className="form-row">
                {input('x', 'Центр X', 'мм')}
                {input('y', 'Центр Y', 'мм')}
                {input('angle', 'Поворот', '°')}
              </div>
              <p className="small text-muted mb-0">
                {editing
                  ? 'Размер фигуры без учёта поворота. Центр и поворот можно не трогать.'
                  : 'Центр сразу стоит посередине лекала. Потом фигуру можно двигать мышью и менять полями сверху.'}
              </p>
              {errors.length > 0 && <div className="alert alert-warning small mt-2 mb-0">{errors.join(' ')}</div>}
            </div>
            <div className="modal-footer">
              <button type="submit" className="btn btn-primary" disabled={errors.length > 0}>
                {editing ? 'Применить' : 'Добавить'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={onCancel}>Отмена</button>
            </div>
          </form>
        </div>
      </div>
      <div className="modal-backdrop fade show" />
    </>
  );
}

function freeName(base, taken, isNew) {
  const used = new Set(taken.map(n => n.trim().toLowerCase()));
  let name = isNew ? base : `${base} (изм.)`;
  for (let i = 2; used.has(name.toLowerCase()); i += 1) name = isNew ? `${base} ${i}` : `${base} (изм. ${i})`;
  return name;
}

function SaveDialog({ variant, mode, onCancel, onSave, onDownload }) {
  const isCreate = mode === 'create';
  const taken = (variant.device.variants || []).map(v => v.name);
  const [name, setName] = useState(() => freeName(isCreate ? NEW_NAME : variant.name, taken, isCreate));
  const [isEnabled, setIsEnabled] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(null);

  useEffect(() => {
    document.body.classList.add('modal-open');
    return () => document.body.classList.remove('modal-open');
  }, []);

  const trimmed = name.trim();
  const clash = taken.some(n => n.trim().toLowerCase() === trimmed.toLowerCase());
  const canSave = trimmed && !clash && !busy;

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const created = await onSave({ name: trimmed, isEnabled });
      setSaved(created);
    } catch (e) {
      setError(e.message || 'Не удалось сохранить');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="modal fade show d-block prn-editor__save" tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-dialog-scrollable" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{saved ? 'Сохранено' : (isCreate ? 'Сохранить лекало' : 'Сохранить как новое лекало')}</h5>
            </div>
            <div className="modal-body">
              {saved ? (
                <p>
                  Лекало «{saved.name}» добавлено в модель {variant.device.name}.
                  {!isCreate && ` Исходное «${variant.name}» не изменилось.`}
                </p>
              ) : (
                <>
                  <p className="text-muted small">
                    {isCreate
                      ? `Лекало появится в модели ${variant.device.name}, с картинкой из контуров.`
                      : `Исходное лекало «${variant.name}» не меняется. Новое появится рядом, в модели ${variant.device.name}, с картинкой из контуров.`}
                  </p>
                  <div className="form-group">
                    <label htmlFor="prn-save-name">Название</label>
                    <input
                      id="prn-save-name"
                      className={`form-control ${clash ? 'is-invalid' : ''}`}
                      value={name}
                      onChange={e => setName(e.target.value)}
                      autoFocus
                    />
                    {clash && <div className="invalid-feedback">Лекало с таким названием в модели уже есть.</div>}
                  </div>
                  <div className="form-check">
                    <input id="prn-save-enabled" type="checkbox" className="form-check-input" checked={isEnabled} onChange={e => setIsEnabled(e.target.checked)} />
                    <label className="form-check-label" htmlFor="prn-save-enabled">
                      Разрешить использование: лекало сразу увидят филиалы
                    </label>
                  </div>
                  {!isEnabled && (
                    <p className="small text-muted mt-2 mb-0">
                      Выключенное лекало филиалы не увидят. Проверить рез можно файлом: кнопка «скачать .prn».
                    </p>
                  )}
                  {error && <div className="alert alert-danger mt-3 mb-0">{error}</div>}
                </>
              )}
            </div>
            <div className="modal-footer">
              {saved ? (
                <>
                  <Link className="btn btn-primary" to={`/library/variants/${variant.device.id}/`}>К лекалам модели</Link>
                  <button type="button" className="btn btn-secondary" onClick={onCancel}>Продолжить правку</button>
                </>
              ) : (
                <>
                  <button type="button" className="btn btn-link mr-auto" onClick={() => onDownload(trimmed || variant.name)}>скачать .prn</button>
                  <button type="button" className="btn btn-primary" disabled={!canSave} onClick={save}>
                    {busy ? 'Сохраняю…' : 'Сохранить'}
                  </button>
                  <button type="button" className="btn btn-secondary" disabled={busy} onClick={onCancel}>Отмена</button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" />
    </>
  );
}
